/**
 * Hero portraits — drawn, not loaded.
 *
 * Death Must Die frames each of its gods in a lit niche with a gilded arch, and
 * the reason it reads so well is not the rendering: it is that every portrait
 * shares one construction — same crop, same light, same frame — so the
 * differences between characters land as *character* rather than as noise. That
 * part is reproducible in vectors, and this file reproduces it.
 *
 * What it is not is painted illustration. These are stylised vector busts with
 * anime cues — the large eye with a hard lash line and a specular dot, the
 * sharp layered hair silhouette, the small nose and mouth — assembled from
 * paths. At portrait size in a menu or a HUD panel they read as characters; at
 * poster size they would read as iconography, because that is what they are.
 *
 * ── What the first pass got wrong, and how ──────────────────────────────────
 *
 * Four faults, all of them invisible in the source and obvious in a render:
 *
 *   • The crop was 1:1, so the head took a third of the niche and the portrait
 *     read as a figure standing in a doorway. Portraits of this kind crop
 *     tight; the head takes about half the height and the shoulders leave the
 *     frame.
 *   • The neck was drawn *after* the head, so its fill covered the jaw and every
 *     face wore a pale slab from the lip down. The head is always the last skin
 *     shape laid.
 *   • The eyes were twice the size they needed and read as alarm.
 *   • Flatly lit they were dolls. A single key from the right, a shadow across
 *     the far cheek and a sink into darkness give the head volume the geometry
 *     does not have.
 *
 * The fifth fault took a second pass, because it is a fault of *sameness* rather
 * than of any one portrait: every face was the same oval with the same eyes at
 * the same height, and only hair and palette told them apart. Five characters
 * differentiated by hair colour are one character with five wigs.
 *
 * So the head is no longer a constant. `SHAPE` gives every hero their own skull
 * width, cheekbone, jaw corner, chin, eye line, eye size, brow weight, mouth and
 * age — and a `tilt`, which is the cheapest attitude lever there is: the Warlord
 * looks at you from under his brow, the Sorcerer looks down his nose, the
 * Prelate has his head back and listening. What stays constant is the crop, the
 * light and the frame. That is the right division: construction shared, anatomy
 * not.
 *
 * The five silhouettes are deliberately five different shapes, because a
 * thumbnail is a shape before it is a face:
 *
 *   Warlord     bare, low and wide — a flat skull on a gorget
 *   Castellan   a smooth dome: mail coif under an iron circlet
 *   Huntmaster  ragged — loose hair and a wolf pelt over the shoulders
 *   Sorcerer    narrow, flanked by two spikes of a raised collar
 *   Prelate     tall — a mitre, and a blindfold where the eyes should be
 *
 * Everything is built from the game's own palette, so a portrait cannot drift
 * out of the world's colour language, and everything is a string, so there are
 * no assets to load, nothing to 404, and a new class costs one entry in `LOOKS`
 * and one in `SHAPE` rather than an art commission.
 */

import { C, mix, shade } from '../data/palette.ts';
import type { HeroArchetype } from '../data/heroes.ts';
import type { HouseLook, RaceFace } from './racefaces.ts';

const hex = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

/* ── Anatomy ─────────────────────────────────────────────────────────────── */

/**
 * A face, as numbers.
 *
 * All in the 100×120 drawing frame, mirrored about x = 50. The head is built
 * from four landmarks down each side — crown, temple, cheekbone, jaw corner —
 * and closed at the chin, which is enough control to get from a slab-jawed
 * soldier to a gaunt ascetic without either of them being an oval.
 */
interface Shape {
  /** Top of the skull. */
  crown: number;
  /** Half-width at the temple, just below the crown. */
  skull: number;
  /** Half-width at the cheekbone, and its height. */
  cheek: number;
  cheekY: number;
  /** Half-width at the jaw corner, and its height. */
  jaw: number;
  jawY: number;
  /** The chin: how low, and how wide across the point. */
  chinY: number;
  chinW: number;

  /** Eye line, half-spacing, and size of the opening. */
  eyeY: number;
  eyeDx: number;
  eyeW: number;
  eyeH: number;
  /** How far the upper lid comes down over the iris, 0..1. Hooded eyes read older and harder. */
  lid: number;
  /** How much the outer corner rises above the inner. Negative droops. */
  slant: number;

  /** Brow: height above the eye, how far it lifts at the outer end, weight of the stroke. */
  browY: number;
  browTilt: number;
  browWeight: number;
  browW: number;

  noseY: number;
  noseW: number;
  mouthY: number;
  mouthW: number;
  /** Positive corners up, negative down. */
  mouthCurve: number;

  /** Half-width of the neck. */
  neck: number;
  /** Degrees. Positive tips the chin up and the head back. */
  tilt: number;
  /** Age marks — folds, bags, forehead lines. 0 is young, 1 is old. */
  age: number;
}

const BASE: Shape = {
  crown: 27,
  skull: 17.6,
  cheek: 17.2,
  cheekY: 52,
  jaw: 13.4,
  jawY: 66,
  chinY: 78.5,
  chinW: 5.4,
  eyeY: 55,
  eyeDx: 6.2,
  eyeW: 4.2,
  eyeH: 2.5,
  lid: 0.22,
  slant: 1.4,
  browY: 9.4,
  browTilt: 1.6,
  browWeight: 1.5,
  browW: 6.2,
  noseY: 63.4,
  noseW: 1.5,
  mouthY: 70,
  mouthW: 4.6,
  mouthCurve: 0.9,
  neck: 6.5,
  tilt: 0,
  age: 0,
};

/**
 * Five heads that are not the same head.
 *
 * The numbers are the whole argument of this file, so they are worth reading as
 * description rather than as data: the Warlord's jaw is two units wider than his
 * cheekbone is tall, the Sorcerer's face is four units longer than the
 * Castellan's on a narrower skull, and the Prelate's chin is both high and wide
 * because a heavy old face collapses upward.
 */
const SHAPE: Record<HeroArchetype, Shape> = {
  /** Slab of a head, set low. Everything about him is wide and close to the bone. */
  warlord: {
    ...BASE,
    crown: 28.5,
    skull: 18.6,
    cheek: 18.4,
    cheekY: 53,
    jaw: 16.4,
    jawY: 67.5,
    chinY: 78,
    // 8.2 made the chin as wide as the jaw and the whole head a rounded brick.
    // He should read as slab-*jawed*, which is a wide corner and a chin that
    // still comes to something.
    chinW: 6.4,
    eyeY: 55.5,
    eyeDx: 6.6,
    eyeW: 4.1,
    eyeH: 1.9,
    lid: 0.5,
    slant: 0.4,
    browY: 7.6,
    browTilt: -1.9,
    browWeight: 2.6,
    browW: 7.2,
    noseY: 64,
    noseW: 2.1,
    mouthY: 71.2,
    mouthW: 5.6,
    mouthCurve: -0.4,
    neck: 8.2,
    tilt: -4,
    age: 0.35,
  },

  /** Oval, level, exact. The only one of the five who is looking straight at you. */
  castellan: {
    ...BASE,
    crown: 27,
    skull: 16.8,
    cheek: 16.6,
    cheekY: 51.5,
    jaw: 12.4,
    jawY: 66,
    chinY: 78.5,
    chinW: 4.8,
    eyeY: 54.5,
    eyeDx: 6.1,
    eyeW: 4.3,
    eyeH: 2.6,
    lid: 0.18,
    slant: 1.7,
    browY: 9.8,
    browTilt: 2.4,
    browWeight: 1.3,
    browW: 6.0,
    noseY: 63,
    noseW: 1.3,
    mouthY: 70,
    mouthW: 4.4,
    mouthCurve: 0.5,
    neck: 6.0,
    tilt: 0,
    age: 0.1,
  },

  /** Long, lean, weathered. Turned a few degrees, as though something moved. */
  huntmaster: {
    ...BASE,
    crown: 27.5,
    skull: 16.6,
    cheek: 16.8,
    cheekY: 53.5,
    jaw: 13.0,
    jawY: 68,
    chinY: 81,
    chinW: 5.2,
    eyeY: 55.5,
    eyeDx: 6.6,
    eyeW: 4.1,
    eyeH: 2.0,
    lid: 0.46,
    slant: 1.0,
    browY: 8.2,
    browTilt: 0.2,
    browWeight: 1.7,
    browW: 6.6,
    noseY: 64.6,
    noseW: 1.5,
    mouthY: 72,
    mouthW: 4.6,
    mouthCurve: -0.2,
    neck: 6.4,
    tilt: -7,
    age: 0.3,
  },

  /** Skull-narrow and four units too long, with the eyes set high and wide open. */
  sorcerer: {
    ...BASE,
    crown: 25.5,
    skull: 15.6,
    cheek: 14.8,
    cheekY: 53,
    jaw: 10.6,
    jawY: 68,
    chinY: 82.5,
    chinW: 3.9,
    eyeY: 54,
    eyeDx: 6.2,
    eyeW: 4.4,
    eyeH: 2.9,
    lid: 0.04,
    slant: 2.1,
    browY: 11.0,
    browTilt: 3.0,
    browWeight: 1.1,
    browW: 5.6,
    noseY: 64,
    noseW: 1.1,
    mouthY: 72.5,
    mouthW: 3.8,
    mouthCurve: -0.5,
    neck: 5.4,
    tilt: 5,
    age: 0.45,
  },

  /** Heavy, jowled, old. Head back: he is listening, not looking. */
  prelate: {
    ...BASE,
    crown: 28,
    skull: 18.0,
    cheek: 17.8,
    cheekY: 53,
    jaw: 15.6,
    jawY: 66.5,
    chinY: 77,
    chinW: 7.6,
    eyeY: 55,
    eyeDx: 6.4,
    eyeW: 4.2,
    eyeH: 2.2,
    lid: 0.5,
    slant: -0.8,
    browY: 8.6,
    browTilt: -0.6,
    browWeight: 1.9,
    browW: 6.8,
    noseY: 64.4,
    noseW: 1.9,
    mouthY: 70.6,
    mouthW: 5.0,
    mouthCurve: -1.1,
    neck: 7.2,
    tilt: 6,
    age: 1,
  },
};

/** Skin, in the same ash-and-bone family as everything else. */
interface Skin {
  base: number;
  shade: number;
  deep: number;
}

/* ── Shared parts ────────────────────────────────────────────────────────── */

/**
 * The head, from the landmarks up.
 *
 * Four cubics down each side rather than an ellipse, because the thing that
 * makes a face read as a face at 40px is the *corner* — the change of direction
 * at the cheekbone and again at the jaw. An oval with features on it is a doll
 * at any size, and was.
 */
function headPath(f: Shape): string {
  const cy = (f.cheekY + f.crown) / 2;
  const r = (x: number): number => 50 + x;
  const l = (x: number): number => 50 - x;
  return (
    `M50 ${f.crown} ` +
    `C${r(f.skull * 0.78)} ${f.crown} ${r(f.skull)} ${cy - 4} ${r(f.cheek)} ${f.cheekY} ` +
    `C${r(f.cheek)} ${f.cheekY + (f.jawY - f.cheekY) * 0.46} ${r(f.jaw * 1.06)} ${f.jawY - (f.jawY - f.cheekY) * 0.3} ${r(f.jaw)} ${f.jawY} ` +
    `C${r(f.jaw)} ${f.jawY + (f.chinY - f.jawY) * 0.52} ${r(f.chinW * 1.7)} ${f.chinY} 50 ${f.chinY} ` +
    `C${l(f.chinW * 1.7)} ${f.chinY} ${l(f.jaw)} ${f.jawY + (f.chinY - f.jawY) * 0.52} ${l(f.jaw)} ${f.jawY} ` +
    `C${l(f.jaw * 1.06)} ${f.jawY - (f.jawY - f.cheekY) * 0.3} ${l(f.cheek)} ${f.cheekY + (f.jawY - f.cheekY) * 0.46} ${l(f.cheek)} ${f.cheekY} ` +
    `C${l(f.skull)} ${cy - 4} ${l(f.skull * 0.78)} ${f.crown} 50 ${f.crown} Z`
  );
}

/**
 * Neck and the shadow the jaw throws on it.
 *
 * Drawn *before* the head, always — see the header. A wedge widening into the
 * trapezius rather than a column, because a column reads as a post.
 */
function neck(f: Shape, s: Skin): string {
  const top = f.jawY - 4;
  return (
    `<path d="M${50 - f.neck} ${top} L${50 - f.neck} ${f.chinY + 5} ` +
    `C${50 - f.neck} ${f.chinY + 10} ${50 - f.neck * 2.4} ${f.chinY + 13} ${50 - f.neck * 3.6} ${f.chinY + 16} ` +
    `L${50 + f.neck * 3.6} ${f.chinY + 16} ` +
    `C${50 + f.neck * 2.4} ${f.chinY + 13} ${50 + f.neck} ${f.chinY + 10} ${50 + f.neck} ${f.chinY + 5} ` +
    `L${50 + f.neck} ${top} Z" fill="${hex(s.shade)}"/>` +
    `<path d="M${50 - f.neck} ${top} L${50 - f.neck} ${f.chinY - 3} ` +
    `C${50 - f.neck * 0.5} ${f.chinY + 1} ${50 + f.neck * 0.5} ${f.chinY + 1} ${50 + f.neck} ${f.chinY - 3} ` +
    `L${50 + f.neck} ${top} Z" fill="${hex(s.deep)}" opacity="0.6"/>`
  );
}

/** Ears, small and set at the cheekbone. Mostly they are covered; when they are not, their absence shows. */
function ears(f: Shape, s: Skin): string {
  const y = f.cheekY + 1;
  const w = f.cheek;
  return (
    `<path d="M${50 + w - 1} ${y - 3} C${50 + w + 3.4} ${y - 3.6} ${50 + w + 4} ${y + 3} ${50 + w + 1.4} ${y + 5} ` +
    `C${50 + w} ${y + 5.6} ${50 + w - 1.2} ${y + 4} ${50 + w - 1} ${y - 3} Z" fill="${hex(s.shade)}"/>` +
    `<path d="M${50 - w + 1} ${y - 3} C${50 - w - 3.4} ${y - 3.6} ${50 - w - 4} ${y + 3} ${50 - w - 1.4} ${y + 5} ` +
    `C${50 - w} ${y + 5.6} ${50 - w + 1.2} ${y + 4} ${50 - w + 1} ${y - 3} Z" fill="${hex(s.shade)}"/>`
  );
}

/**
 * The lit and shaded halves of the head, so the key light has something to land
 * on.
 *
 * The core shadow used to be a flat fill at 0.4, and its inner edge — a single
 * cubic running from the crown to the chin — landed just left of the nose as a
 * hard, near-vertical seam down the middle of every face. At 230px it read as a
 * mask edge; at 104 it read as dirt. It is now the same shape filled with a
 * gradient that dies out before it reaches the centre line, which is what a
 * shadow on a curved surface actually does.
 */
function modelling(f: Shape, s: Skin, uid: string): string {
  const d = headPath(f);
  return (
    `<defs><linearGradient id="${uid}-core" x1="0" y1="0" x2="1" y2="0">` +
    `<stop offset="0%" stop-color="${hex(s.deep)}" stop-opacity="0.5"/>` +
    `<stop offset="55%" stop-color="${hex(s.deep)}" stop-opacity="0.22"/>` +
    `<stop offset="100%" stop-color="${hex(s.deep)}" stop-opacity="0"/>` +
    `</linearGradient></defs>` +
    `<path d="${d}" fill="${hex(s.base)}"/>` +
    `<path d="M50 ${f.crown} C${50 - f.skull * 0.78} ${f.crown} ${50 - f.skull} ${(f.cheekY + f.crown) / 2 - 4} ${50 - f.cheek} ${f.cheekY} ` +
    `C${50 - f.cheek} ${f.cheekY + (f.jawY - f.cheekY) * 0.46} ${50 - f.jaw * 1.06} ${f.jawY - (f.jawY - f.cheekY) * 0.3} ${50 - f.jaw} ${f.jawY} ` +
    `C${50 - f.jaw} ${f.jawY + (f.chinY - f.jawY) * 0.52} ${50 - f.chinW * 1.7} ${f.chinY} 50 ${f.chinY} ` +
    `C${50 - f.chinW * 0.4} ${f.chinY - 4} ${50 - f.cheek * 0.42} ${f.jawY - 2} ${50 - f.cheek * 0.5} ${f.cheekY - 4} ` +
    `C${50 - f.cheek * 0.55} ${f.crown + 6} ${50 - f.skull * 0.4} ${f.crown + 1} 50 ${f.crown} Z" ` +
    `fill="url(#${uid}-core)"/>` +
    // Cheekbone catch on the lit side: two units of pale is the whole difference
    // between a head and a mask.
    `<ellipse cx="${50 + f.cheek * 0.58}" cy="${f.cheekY + 3}" rx="${f.cheek * 0.3}" ry="3.2" ` +
    `fill="#fff" opacity="0.1"/>`
  );
}

/**
 * Eyes, and they carry the whole expression.
 *
 * Three things in a fixed relationship make the read: a heavy dark upper lash
 * thickest at the outer corner, an iris large enough to touch both lids, and a
 * single hard specular dot placed off-centre. Move the dot to the middle and the
 * face dies.
 *
 * The lid is a skin-coloured shutter clipped to the eye opening. That is what
 * separates the Sorcerer, whose lids are nowhere near his irises, from the
 * Warlord, who is looking at you through a two-unit gap.
 */
function almond(cx: number, cy: number, w: number, h: number, slant: number, flip: number): string {
  // `h` is the half-height and is used as such — the first cut multiplied it by
  // 1.5 on the way up and 1.6 on the way down, which made every eye as tall as
  // it was wide. Five circular eyes is why the whole set read as alarm.
  return (
    `M${cx - w * flip} ${cy + 0.5} ` +
    `C${cx - w * 0.5 * flip} ${cy - h * 1.05} ${cx + w * 0.55 * flip} ${cy - h * 1.1 - slant * 0.4} ${cx + w * flip} ${cy - slant} ` +
    `C${cx + w * 0.7 * flip} ${cy + h * 0.72} ${cx - w * 0.45 * flip} ${cy + h * 0.85} ${cx - w * flip} ${cy + 0.5} Z`
  );
}

/**
 * The pupil, and it is the cheapest race tell in the whole record.
 *
 * At 46px the entire eye is three pixels across, so nothing about its *shape*
 * survives — but a vertical slit against a coloured iris survives, because it is
 * a change of value in the middle of a bright patch. One path, no geometry, and
 * it reads as reptile before the viewer has looked at the skull.
 */
function pupilArt(cx: number, cy: number, w: number, h: number, kind: NonNullable<FaceLook['pupil']>): string {
  const ink = hex(0x120e0c);
  switch (kind) {
    case 'none':
      return '';
    case 'slit':
      return `<ellipse cx="${cx}" cy="${cy}" rx="${w * 0.08}" ry="${h * 0.95}" fill="${ink}"/>`;
    case 'goat':
      // A horizontal bar. Prey, not predator, and unmistakably neither human.
      return `<rect x="${cx - w * 0.34}" y="${cy - h * 0.2}" width="${w * 0.68}" height="${h * 0.4}" rx="${h * 0.16}" fill="${ink}"/>`;
    case 'pinpoint':
      return `<circle cx="${cx}" cy="${cy}" r="${Math.max(0.35, w * 0.1)}" fill="${ink}"/>`;
    default:
      return `<ellipse cx="${cx}" cy="${cy}" rx="${w * 0.2}" ry="${h * 0.62}" fill="${ink}"/>`;
  }
}

type Gaze = 'open' | 'narrow' | 'shut' | 'blind' | 'aside';

/**
 * The part of a `Look` that eyes and brows actually read.
 *
 * Narrowed out of `Look` so a leader portrait can hand these five fields over
 * without having to fabricate the six drawing closures it does not use. `Look`
 * satisfies it structurally, so every hero call site is unchanged.
 *
 * The three optional fields are the race ones, and they default to human. This
 * is the whole reason a slit pupil or an all-black eye is cheap: it is a branch
 * inside a function that already exists, not a second eye renderer.
 */
interface FaceLook {
  hair: number;
  brow?: number;
  eye: number;
  cloth: number;
  gaze: Gaze;
  pupil?: RaceFace['pupil'];
  sclera?: RaceFace['sclera'];
  /** 0 an ordinary eye, 1 the eye is the light source in the picture. */
  glow?: number;
}

function eyes(f: Shape, l: FaceLook, s: Skin, uid: string): string {
  const y = f.eyeY;

  if (l.gaze === 'blind') {
    // A bound cloth. At forty pixels a pair of closed lids is nothing at all;
    // a band across the face is a fact you can read across a room.
    return (
      `<path d="M${50 - f.cheek - 1} ${y - 4.6} L${50 + f.cheek + 1} ${y - 5.8} L${50 + f.cheek + 1} ${y + 3.4} ` +
      `L${50 - f.cheek - 1} ${y + 4.6} Z" fill="${hex(l.cloth)}"/>` +
      `<path d="M${50 - f.cheek - 1} ${y - 4.6} L${50 + f.cheek + 1} ${y - 5.8} L${50 + f.cheek + 1} ${y - 3.4} ` +
      `L${50 - f.cheek - 1} ${y - 2.2} Z" fill="#fff" opacity="0.16"/>` +
      `<path d="M${50 - f.cheek - 1} ${y + 1.4} L${50 + f.cheek + 1} ${y + 0.2}" stroke="#000" ` +
      `stroke-width="0.7" opacity="0.35"/>` +
      // The sockets under it, so the cloth sits on a face rather than floating.
      `<path d="M${50 - f.eyeDx - f.eyeW} ${y + 4.2} C${50 - f.eyeDx} ${y + 6.4} ${50 - f.eyeDx + f.eyeW} ${y + 5.4} ${50 - f.eyeDx + f.eyeW + 1} ${y + 4}" ` +
      `stroke="${hex(s.deep)}" stroke-width="0.8" fill="none" opacity="0.5"/>` +
      `<path d="M${50 + f.eyeDx + f.eyeW} ${y + 3.6} C${50 + f.eyeDx} ${y + 6} ${50 + f.eyeDx - f.eyeW} ${y + 5} ${50 + f.eyeDx - f.eyeW - 1} ${y + 3.4}" ` +
      `stroke="${hex(s.deep)}" stroke-width="0.8" fill="none" opacity="0.5"/>` +
      brows(f, l)
    );
  }

  if (l.gaze === 'shut') {
    return (
      `<path d="M${50 - f.eyeDx - f.eyeW} ${y} C${50 - f.eyeDx} ${y + 3.2} ${50 - f.eyeDx + f.eyeW} ${y + 2.6} ${50 - f.eyeDx + f.eyeW + 0.6} ${y - 0.6}" ` +
      `stroke="${hex(0x2a2320)}" stroke-width="1.5" fill="none" stroke-linecap="round"/>` +
      `<path d="M${50 + f.eyeDx + f.eyeW} ${y} C${50 + f.eyeDx} ${y + 3.2} ${50 + f.eyeDx - f.eyeW} ${y + 2.6} ${50 + f.eyeDx - f.eyeW - 0.6} ${y - 0.6}" ` +
      `stroke="${hex(0x2a2320)}" stroke-width="1.5" fill="none" stroke-linecap="round"/>` +
      brows(f, l)
    );
  }

  const w = f.eyeW;
  const h = f.eyeH;
  const lidDrop = l.gaze === 'narrow' ? Math.max(f.lid, 0.45) : f.lid;
  // Looking away. Both irises go the *same* way — mirrored, they cross, and a
  // portrait that squints is not a portrait that is thinking about something
  // else. It is the cheapest bearing in the record and one of the strongest.
  const aside = l.gaze === 'aside' ? w * 0.5 : 0;
  const sclera = l.sclera ?? 'white';
  const pupil = l.pupil ?? 'round';
  const glow = l.glow ?? 0;
  const one = (cx: number, flip: number): string => {
    const irisY = y - 0.5 + lidDrop * h * 0.6;
    const ix = cx + 0.3 * flip + aside;
    return (
      `<path d="${almond(cx, y, w, h, f.slant, flip)}" fill="${hex(sclera === 'dark' ? 0x14161c : 0xf4eee2)}"/>` +
      `<g clip-path="url(#${uid}-eyeclip)">` +
      `<ellipse cx="${ix}" cy="${irisY}" rx="${w * 0.42}" ry="${h * 1.15}" fill="${hex(l.eye)}"/>` +
      `<ellipse cx="${ix}" cy="${irisY - h * 0.4}" rx="${w * 0.42}" ry="${h * 0.8}" fill="#000" opacity="0.22"/>` +
      pupilArt(ix, irisY, w, h, pupil) +
      // The lid, as a shutter in skin. This is the knob that ages a face.
      `<path d="M${cx - w * 1.3} ${y - h * 2.6} L${cx + w * 1.3} ${y - h * 2.6} ` +
      `L${cx + w * 1.3} ${y - h * 1.15 + lidDrop * h * 2.0 - f.slant * flip * 0.35} ` +
      `L${cx - w * 1.3} ${y - h * 0.95 + lidDrop * h * 2.0 + f.slant * flip * 0.35} Z" fill="${hex(s.base)}"/>` +
      `</g>` +
      // Lash: heaviest at the outer corner, with a flick past it.
      `<path d="${almond(cx, y, w, h, f.slant, flip)}" fill="none" stroke="${hex(0x231c18)}" ` +
      `stroke-width="0.7" opacity="0.75"/>` +
      `<path d="M${cx - w * flip} ${y + 0.4} C${cx - w * 0.5 * flip} ${y - h * 1.05} ${cx + w * 0.55 * flip} ${y - h * 1.1 - f.slant * 0.4} ${cx + w * flip} ${y - f.slant} ` +
      `L${cx + w * 1.32 * flip} ${y - f.slant - 1.5} ` +
      `C${cx + w * 0.55 * flip} ${y - h * 1.1 - f.slant * 0.4 - 1.7} ${cx - w * 0.5 * flip} ${y - h * 1.05 - 1.7} ${cx - w * flip} ${y + 0.4 - 1.4} Z" ` +
      `fill="${hex(0x1e1815)}"/>` +
      `<circle cx="${ix - w * 0.34}" cy="${irisY - h * 0.45}" r="${Math.max(0.6, w * 0.16)}" fill="#fff" opacity="0.95"/>` +
      // Lower lid, one hairline. Without it the eye floats off the cheek.
      `<path d="M${cx - w * 0.85 * flip} ${y + h * 0.6} C${cx} ${y + h * 1.05} ${cx + w * 0.6 * flip} ${y + h * 0.8} ${cx + w * 0.95 * flip} ${y + h * 0.15}" ` +
      `stroke="${hex(s.deep)}" stroke-width="0.6" fill="none" opacity="0.55"/>`
    );
  };

  if (sclera === 'none') {
    // No eye at all — a socket, and whatever is alight at the back of it. This
    // is worth more at 46px than any amount of anatomy: a lit point behind a
    // dark face reads instantly and reads as *wrong*, which is the intent.
    const c = glow > 0 ? l.eye : 0x1a1712;
    const socket = (cx: number): string =>
      `<ellipse cx="${cx}" cy="${y}" rx="${w * 1.05}" ry="${h * 1.7}" fill="${hex(0x0d0b09)}"/>` +
      (glow > 0
        ? `<ellipse cx="${cx}" cy="${y}" rx="${w * 0.95}" ry="${h * 1.4}" fill="${hex(c)}" opacity="${(glow * 0.4).toFixed(2)}"/>` +
          `<ellipse cx="${cx}" cy="${y + h * 0.2}" rx="${w * 0.34}" ry="${h * 0.6}" fill="${hex(mix(c, 0xffffff, 0.55))}" opacity="${(0.55 + glow * 0.45).toFixed(2)}"/>`
        : '');
    return socket(50 - f.eyeDx) + socket(50 + f.eyeDx) + brows(f, l);
  }

  return (
    `<defs><clipPath id="${uid}-eyeclip">` +
    `<path d="${almond(50 - f.eyeDx, y, w, h, f.slant, 1)}"/>` +
    `<path d="${almond(50 + f.eyeDx, y, w, h, f.slant, -1)}"/>` +
    `</clipPath></defs>` +
    one(50 - f.eyeDx, 1) +
    one(50 + f.eyeDx, -1) +
    (glow > 0
      ? `<ellipse cx="${50 - f.eyeDx}" cy="${y}" rx="${w * 1.5}" ry="${h * 1.8}" fill="${hex(l.eye)}" opacity="${(glow * 0.3).toFixed(2)}"/>` +
        `<ellipse cx="${50 + f.eyeDx}" cy="${y}" rx="${w * 1.5}" ry="${h * 1.8}" fill="${hex(l.eye)}" opacity="${(glow * 0.3).toFixed(2)}"/>`
      : '') +
    brows(f, l)
  );
}

/**
 * Brows sit close to the lid. A high brow reads as surprise, and none of these
 * five are surprised by anything.
 *
 * Two corrections from the 230px sheet. `browY` was being used at face value, so
 * every brow floated eight units clear of its eye with a wide band of blank
 * forehead between — the read was startled, not stern. And `browWeight` at full
 * put a stroke two and a half units thick on a face fourteen units across, which
 * is a dark slug, and five dark slugs at the same height is more of a shared
 * signature than any of the five head shapes underneath. `browY` still has to
 * stay as it is because the hairlines are measured off it; the brow itself now
 * sits at 70% of that gap and carries three quarters of the weight.
 */
function brows(f: Shape, l: FaceLook): string {
  const y = f.eyeY - f.browY * 0.7;
  const inner = 1.6;
  const w = f.browWeight * 0.76;
  return (
    `<path d="M${50 - f.eyeDx - f.browW} ${y - f.browTilt} C${50 - f.eyeDx} ${y - f.browTilt - 1.4} ` +
    `${50 - inner - 2} ${y - 0.4} ${50 - inner} ${y + 0.6}" stroke="${hex(l.brow ?? l.hair)}" ` +
    `stroke-width="${w}" fill="none" stroke-linecap="round"/>` +
    `<path d="M${50 + f.eyeDx + f.browW} ${y - f.browTilt} C${50 + f.eyeDx} ${y - f.browTilt - 1.4} ` +
    `${50 + inner + 2} ${y - 0.4} ${50 + inner} ${y + 0.6}" stroke="${hex(l.brow ?? l.hair)}" ` +
    `stroke-width="${w}" fill="none" stroke-linecap="round"/>`
  );
}

/** Nose, mouth, and whatever the years have added. As little as will still read. */
function features(f: Shape, s: Skin, o: { muzzle?: number; lip?: number } = {}): string {
  // A muzzle supplies its own nose and its own mouth, and a people with no lips
  // gets a line rather than a shape. Both are leader-only and both default off,
  // so every hero call is unchanged.
  const noNose = (o.muzzle ?? 0) > 0.15;
  const lipMul = o.lip ?? 1;
  // The nose, and the lesson here cost two renders.
  //
  // It began as one faint ellipse, which is a smudge. It was then drawn properly
  // — a filled wedge from the bridge out to both wings — and every one of the
  // five grew a pale rounded lump between the eyes and the mouth that reads,
  // unmistakably, as a snout. At this level of stylisation a nose cannot be
  // *modelled*; it can only be implied, and the implication that works is a
  // shadow under the tip plus two small wings. No mass, no outline.
  const nw = f.noseW;
  const nostril =
    `<path d="M${50 - nw * 1.9} ${f.noseY - 0.4} C${50 - nw * 2.1} ${f.noseY + 1.4} ${50 - nw * 0.9} ${f.noseY + 2} ${50 - nw * 0.3} ${f.noseY + 1.4} ` +
    `C${50 + nw * 0.3} ${f.noseY + 1.4} ${50 + nw * 2.1} ${f.noseY + 1.6} ${50 + nw * 1.9} ${f.noseY - 0.4} ` +
    `C${50 + nw * 1.2} ${f.noseY + 1} ${50 - nw * 1.2} ${f.noseY + 1} ${50 - nw * 1.9} ${f.noseY - 0.4} Z" ` +
    `fill="${hex(s.deep)}" opacity="0.34"/>` +
    `<ellipse cx="${50 - nw * 1.25}" cy="${f.noseY + 0.2}" rx="${nw * 0.44}" ry="${nw * 0.34}" fill="${hex(s.deep)}" opacity="0.55"/>` +
    `<ellipse cx="${50 + nw * 1.25}" cy="${f.noseY + 0.2}" rx="${nw * 0.44}" ry="${nw * 0.34}" fill="${hex(s.deep)}" opacity="0.55"/>`;
  // The bridge: a shadow down the far side and a catch of light down the near
  // one. Together they are what turn two nostrils into a nose.
  const bridge =
    `<path d="M${50 - nw * 0.8} ${f.eyeY + 1} C${50 - nw * 1.6} ${f.noseY - 6} ` +
    `${50 - nw * 1.9} ${f.noseY - 3} ${50 - nw * 2} ${f.noseY - 0.6}" ` +
    `stroke="${hex(s.deep)}" stroke-width="0.85" fill="none" opacity="0.42"/>` +
    `<path d="M${50 + nw * 0.5} ${f.eyeY + 1.5} C${50 + nw * 0.9} ${f.noseY - 6} ` +
    `${50 + nw * 1.1} ${f.noseY - 4} ${50 + nw * 0.9} ${f.noseY - 2.6}" ` +
    `stroke="#fff" stroke-width="0.9" fill="none" opacity="0.16" stroke-linecap="round"/>`;
  // A mouth, not a line. An upper lip in shadow and a lit lower one is two
  // shapes and reads as flesh; one stroke of dull red reads as a seam, which is
  // what every one of the five had.
  const lipY = f.mouthY;
  const mw = f.mouthW;
  const cv = f.mouthCurve;
  const mouth =
    `<path d="M${50 - mw} ${lipY} C${50 - mw * 0.5} ${lipY - 1.5 + cv * 0.4} ${50 - 1} ${lipY - 1.2} 50 ${lipY - 0.9} ` +
    `C${50 + 1} ${lipY - 1.2} ${50 + mw * 0.5} ${lipY - 1.5 + cv * 0.4} ${50 + mw} ${lipY} ` +
    `C${50 + mw * 0.5} ${lipY + cv * 0.5} ${50 - mw * 0.5} ${lipY + cv * 0.5} ${50 - mw} ${lipY} Z" ` +
    `fill="${hex(0x6d4740)}" opacity="0.8"/>` +
    `<path d="M${50 - mw * 0.82} ${lipY + 0.5} C${50 - mw * 0.4} ${lipY + 1.6 + cv * 0.6} ${50 + mw * 0.4} ${lipY + 1.6 + cv * 0.6} ${50 + mw * 0.82} ${lipY + 0.5} ` +
    `C${50 + mw * 0.4} ${lipY + 0.9} ${50 - mw * 0.4} ${lipY + 0.9} ${50 - mw * 0.82} ${lipY + 0.5} Z" ` +
    `fill="${hex(0x9c6d5c)}" opacity="0.45"/>` +
    `<path d="M${50 - mw} ${lipY} C${50 - mw * 0.4} ${lipY + cv} ${50 + mw * 0.4} ${lipY + cv} ${50 + mw} ${lipY}" ` +
    `stroke="${hex(0x4c3129)}" stroke-width="${0.75 + f.age * 0.3}" fill="none" stroke-linecap="round" opacity="0.75"/>`;
  // A mouth for the things that have no lips — bone, chitin, a beak. One cut
  // across, with the teeth showing as ticks. A filled lip on a skull is worse
  // than no mouth at all.
  const thinMouth =
    `<path d="M${50 - mw} ${lipY} C${50 - mw * 0.4} ${lipY + cv} ${50 + mw * 0.4} ${lipY + cv} ${50 + mw} ${lipY}" ` +
    `stroke="${hex(0x241c18)}" stroke-width="1.1" fill="none" stroke-linecap="round"/>` +
    `<path d="M${50 - mw * 0.55} ${lipY + 0.2} v1.5 M50 ${lipY + 0.7} v1.5 M${50 + mw * 0.55} ${lipY + 0.2} v1.5" ` +
    `stroke="${hex(s.base)}" stroke-width="0.6" opacity="0.75"/>`;
  // The crease under the lower lip, and nothing else. There used to be a wide
  // ellipse here as well, and between it and the nasolabial folds every face
  // wore a closed bracket around its mouth — a muzzle, at any size.
  const chin =
    `<path d="M${50 - f.chinW * 0.5} ${f.mouthY + 3.2} C50 ${f.mouthY + 4.4} 50 ${f.mouthY + 4.4} ${50 + f.chinW * 0.5} ${f.mouthY + 3.2}" ` +
    `stroke="${hex(s.deep)}" stroke-width="0.7" fill="none" opacity="0.28"/>`;

  const nose = noNose ? '' : `${bridge}${nostril}`;
  const lips = lipMul <= 0.05 ? thinMouth : mouth;
  if (f.age < 0.25) return `${nose}${lips}${chin}`;

  const a = f.age;
  // Nasolabial folds, eye bags, forehead lines. Drawn faint and only when
  // earned: an age mark on a young face reads as dirt. They now stop level with
  // the mouth corner instead of curving back in under it — a fold that hooks
  // inward closes the bracket, and a closed bracket is a muzzle.
  const folds =
    `<path d="M${50 - f.noseW - 1.6} ${f.noseY + 0.4} C${50 - f.mouthW - 2.4} ${f.noseY + 3.6} ` +
    `${50 - f.mouthW - 2.6} ${f.mouthY - 2} ${50 - f.mouthW - 2} ${f.mouthY + 0.4}" ` +
    `stroke="${hex(s.deep)}" stroke-width="0.8" fill="none" opacity="${(0.2 + a * 0.22).toFixed(2)}"/>` +
    `<path d="M${50 + f.noseW + 1.6} ${f.noseY + 0.4} C${50 + f.mouthW + 2.4} ${f.noseY + 3.6} ` +
    `${50 + f.mouthW + 2.6} ${f.mouthY - 2} ${50 + f.mouthW + 2} ${f.mouthY + 0.4}" ` +
    `stroke="${hex(s.deep)}" stroke-width="0.8" fill="none" opacity="${(0.15 + a * 0.2).toFixed(2)}"/>`;
  const bags =
    a < 0.5
      ? ''
      : `<path d="M${50 - f.eyeDx - f.eyeW * 0.7} ${f.eyeY + f.eyeH * 1.9} C${50 - f.eyeDx} ${f.eyeY + f.eyeH * 3} ${50 - f.eyeDx + f.eyeW} ${f.eyeY + f.eyeH * 2.4} ${50 - f.eyeDx + f.eyeW + 1} ${f.eyeY + f.eyeH * 1.4}" ` +
        `stroke="${hex(s.deep)}" stroke-width="0.7" fill="none" opacity="0.4"/>` +
        `<path d="M${50 + f.eyeDx + f.eyeW * 0.7} ${f.eyeY + f.eyeH * 1.9} C${50 + f.eyeDx} ${f.eyeY + f.eyeH * 3} ${50 + f.eyeDx - f.eyeW} ${f.eyeY + f.eyeH * 2.4} ${50 + f.eyeDx - f.eyeW - 1} ${f.eyeY + f.eyeH * 1.4}" ` +
        `stroke="${hex(s.deep)}" stroke-width="0.7" fill="none" opacity="0.34"/>`;
  const lines =
    a < 0.7
      ? ''
      : `<path d="M${50 - f.skull * 0.55} ${f.eyeY - f.browY - 5} C50 ${f.eyeY - f.browY - 7} 50 ${f.eyeY - f.browY - 7} ${50 + f.skull * 0.55} ${f.eyeY - f.browY - 5.6}" ` +
        `stroke="${hex(s.deep)}" stroke-width="0.7" fill="none" opacity="0.3"/>` +
        `<path d="M${50 - f.skull * 0.5} ${f.eyeY - f.browY - 9} C50 ${f.eyeY - f.browY - 11} 50 ${f.eyeY - f.browY - 11} ${50 + f.skull * 0.5} ${f.eyeY - f.browY - 9.6}" ` +
        `stroke="${hex(s.deep)}" stroke-width="0.6" fill="none" opacity="0.22"/>` +
        // Jowls: the chin's shadow, doubled and hung.
        `<path d="M${50 - f.jaw + 0.5} ${f.jawY + 1} C${50 - f.jaw} ${f.chinY - 3} ${50 - f.chinW * 2} ${f.chinY - 1} ${50 - f.chinW * 1.2} ${f.chinY - 2}" ` +
        `stroke="${hex(s.deep)}" stroke-width="0.7" fill="none" opacity="0.3"/>` +
        `<path d="M${50 + f.jaw - 0.5} ${f.jawY + 1} C${50 + f.jaw} ${f.chinY - 3} ${50 + f.chinW * 2} ${f.chinY - 1} ${50 + f.chinW * 1.2} ${f.chinY - 2}" ` +
        `stroke="${hex(s.deep)}" stroke-width="0.7" fill="none" opacity="0.26"/>`;

  return `${nose}${lips}${chin}${folds}${bags}${lines}`;
}

/* ── The five ────────────────────────────────────────────────────────────── */

interface Look {
  /** The one saturated colour this character is allowed. */
  accent: number;
  hair: number;
  hairLight: number;
  /** Brow colour, where it differs from the hair — a white-haired man still has dark brows. */
  brow?: number;
  /** Iris. */
  eye: number;
  cloth: number;
  clothLight: number;
  /** Metal on the shoulders, collar or crown. */
  metal: number;
  skin: Skin;
  gaze: Gaze;
  /** How tight the crop is, and how far down the figure sits. Tall headgear needs room. */
  crop: number;
  cropDy: number;
  /** Behind the shoulders, unrotated: mantles, pelts, wings. */
  beneath?: (l: Look, f: Shape) => string;
  /** Behind the head and turning with it: hood, mitre, coif. */
  behind?: (l: Look, f: Shape) => string;
  /** The hair silhouette, drawn behind the face. */
  hairBack: (l: Look, f: Shape) => string;
  /** Hair over the brow, drawn in front of the face. */
  hairFront: (l: Look, f: Shape) => string;
  /** Shoulders, collar and armour. */
  body: (l: Look, f: Shape) => string;
  /** Anything on top: scars, a veil, a glow. */
  over?: (l: Look, f: Shape) => string;
}

/**
 * The Prelate's mitre.
 *
 * Split out of `LOOKS` because it is worn over the head rather than behind it,
 * so it belongs to `hairFront`, where a forty-line inline expression would bury
 * the beard that follows it.
 *
 * The first one was a shallow dome with vertical sides, a straight gold band cut
 * across it and two straight gold-tipped lappets hanging past the ears — at
 * 230px the whole assembly read as scaffolding: two posts and a lintel around a
 * head. Three faults, all silhouette. It was too short to be a mitre; its sides
 * were vertical where a mitre's flare outward past the skull; and its top was
 * rounded where a mitre's is a *point*. It is now two thirds of a head tall,
 * wider at the brow than the skull is, and pointed. Nothing else in the game has
 * that outline, which at 30px is the only thing about him that survives.
 */
function mitre(l: { cloth: number; clothLight: number; metal: number }, f: Shape): string {
  const base = f.crown - 1;
  // The arch tops out at y=6 and the crop puts the peak straight through it, so
  // the height is set by the headroom the frame has, not by taste.
  const apex = f.crown - 19;
  const w = f.skull + 5;
  return (
    `<path d="M${50 - w} ${base + 3} C${50 - w + 1} ${apex + 16} ${50 - 6} ${apex + 5} 50 ${apex} ` +
    `C${50 + 6} ${apex + 5} ${50 + w - 1} ${apex + 16} ${50 + w} ${base + 3} Z" fill="${hex(l.cloth)}"/>` +
    `<path d="M50 ${apex} C${50 + 6} ${apex + 5} ${50 + w - 1} ${apex + 16} ${50 + w} ${base + 3} L50 ${base + 3} Z" ` +
    `fill="${hex(l.clothLight)}" opacity="0.55"/>` +
    // The seam, and the orphrey cross on it. Without the cross it is a hat.
    `<path d="M50 ${apex + 2} L50 ${base + 3}" stroke="${hex(l.metal)}" stroke-width="1.4" opacity="0.9"/>` +
    `<path d="M${50 - 5.5} ${apex + 8.5} L${50 + 5.5} ${apex + 8.5} L${50 + 5.5} ${apex + 11} L${50 - 5.5} ${apex + 11} Z" fill="${hex(l.metal)}" opacity="0.85"/>` +
    `<path d="M50 ${apex + 4} L${50 + 3.2} ${apex + 9.8} L50 ${apex + 15} L${50 - 3.2} ${apex + 9.8} Z" fill="${hex(C.goldPale)}"/>` +
    // The band at the brow, following the flare rather than cutting straight
    // across it — the straight version was the lintel.
    `<path d="M${50 - w - 0.5} ${base + 2} C${50 - w * 0.4} ${base - 1} ${50 + w * 0.4} ${base - 1} ${50 + w + 0.5} ${base + 2} ` +
    `L${50 + w + 0.5} ${base + 5} C${50 + w * 0.4} ${base + 2} ${50 - w * 0.4} ${base + 2} ${50 - w - 0.5} ${base + 5} Z" ` +
    `fill="${hex(l.metal)}"/>` +
    `<path d="M50 ${base} C${50 + w * 0.5} ${base} ${50 + w} ${base + 1.4} ${50 + w + 0.5} ${base + 2} ` +
    `C${50 + w * 0.4} ${base + 0.4} ${50 + 2} ${base + 1} 50 ${base} Z" fill="${hex(C.goldPale)}" opacity="0.7"/>`
  );
}

const LOOKS: Record<HeroArchetype, Look> = {
  /**
   * Promoted in the field by men who had run out of officers.
   *
   * Bare-headed on purpose: he is the only one of the five with nothing on his
   * head, which at thumbnail size is itself an identity. What he has instead is
   * width — a gorget that runs off both edges of the frame and a jaw nearly as
   * wide as his skull.
   */
  warlord: {
    accent: C.oxbloodLight,
    hair: 0x2b231f,
    hairLight: 0x4a3c33,
    eye: 0xa87b39,
    cloth: 0x4a3129,
    clothLight: 0x6b4739,
    metal: 0x8f8a82,
    skin: { base: 0xc7a284, shade: 0x9e7d61, deep: 0x74533d },
    gaze: 'narrow',
    crop: 1.34,
    cropDy: 3,
    hairBack: (l, f) =>
      `<path d="M${50 - f.skull - 1} ${f.cheekY + 2} C${50 - f.skull - 2} ${f.crown - 6} ${50 - f.skull * 0.6} ${f.crown - 8} 50 ${f.crown - 8} ` +
      `C${50 + f.skull * 0.6} ${f.crown - 8} ${50 + f.skull + 2} ${f.crown - 6} ${50 + f.skull + 1} ${f.cheekY + 2} ` +
      `C${50 + f.skull} ${f.crown + 4} ${50 + f.skull * 0.5} ${f.crown + 1} 50 ${f.crown + 1} ` +
      `C${50 - f.skull * 0.5} ${f.crown + 1} ${50 - f.skull} ${f.crown + 4} ${50 - f.skull - 1} ${f.cheekY + 2} Z" fill="${hex(l.hair)}"/>`,
    /**
     * Cropped to the skull and receding at the temples. Nothing decorative
     * survives a campaign.
     *
     * The hairline had it backwards: low at the temples and high in the middle,
     * which is not a receding hairline, it is a bowl cut with a dip in it. A
     * head goes bare at the corners first, so the temples are now the high
     * points and the peak comes down in the middle.
     */
    hairFront: (l, f) =>
      `<path d="M${50 - f.skull - 0.5} ${f.eyeY - f.browY - 9} C${50 - f.skull - 1} ${f.crown - 5} ${50 - f.skull * 0.55} ${f.crown - 7.5} 50 ${f.crown - 7.5} ` +
      `C${50 + f.skull * 0.55} ${f.crown - 7.5} ${50 + f.skull + 1} ${f.crown - 5} ${50 + f.skull + 0.5} ${f.eyeY - f.browY - 9} ` +
      `C${50 + f.skull * 0.6} ${f.eyeY - f.browY - 7} ${50 + f.skull * 0.3} ${f.eyeY - f.browY - 3.4} ${50 + 0.5} ${f.eyeY - f.browY - 3} ` +
      `C${50 - f.skull * 0.3} ${f.eyeY - f.browY - 3.4} ${50 - f.skull * 0.6} ${f.eyeY - f.browY - 7} ${50 - f.skull - 0.5} ${f.eyeY - f.browY - 9} Z" fill="${hex(l.hair)}"/>` +
      `<path d="M${50 - 6} ${f.crown - 6.6} C${50 - 2} ${f.crown - 8.2} ${50 + 3} ${f.crown - 8.2} ${50 + 7} ${f.crown - 6.4} ` +
      `C${50 + 2} ${f.crown - 5.4} ${50 - 2} ${f.crown - 5.4} ${50 - 6} ${f.crown - 6.6} Z" fill="${hex(l.hairLight)}" opacity="0.7"/>` +
      // Two days of beard along the jaw, as a value not a texture.
      `<path d="M${50 - f.jaw - 0.5} ${f.jawY - 2} C${50 - f.jaw} ${f.chinY - 1} ${50 - f.chinW * 1.8} ${f.chinY + 1} 50 ${f.chinY + 1} ` +
      `C${50 + f.chinW * 1.8} ${f.chinY + 1} ${50 + f.jaw} ${f.chinY - 1} ${50 + f.jaw + 0.5} ${f.jawY - 2} ` +
      `C${50 + f.jaw} ${f.jawY + 5} ${50 + f.chinW * 1.4} ${f.chinY - 4} 50 ${f.chinY - 4} ` +
      `C${50 - f.chinW * 1.4} ${f.chinY - 4} ${50 - f.jaw} ${f.jawY + 5} ${50 - f.jaw - 0.5} ${f.jawY - 2} Z" ` +
      // 0.42 was dark enough to close a ring round the whole face together with
      // the hairline, and a closed ring reads as a mask. Stubble is a value, and
      // a value you notice is not stubble.
      `fill="${hex(l.hair)}" opacity="0.26"/>`,
    body: (l) =>
      // Wider than the frame at the shoulder line, which is the point of him.
      `<path d="M6 120 C6 98 22 86 50 86 C78 86 94 98 94 120 Z" fill="${hex(l.cloth)}"/>` +
      // The gorget: a hard horizontal that says soldier before it says anything else.
      `<path d="M28 92 C36 86 64 86 72 92 L74 100 C64 94 36 94 26 100 Z" fill="${hex(l.metal)}"/>` +
      `<path d="M28 92 C36 86 64 86 72 92 L73 96 C64 91 36 91 27 96 Z" fill="${hex(C.goldPale)}" opacity="0.45"/>` +
      `<path d="M4 120 C4 100 14 90 26 86 C22 96 16 106 16 120 Z" fill="${hex(C.goldDeep)}"/>` +
      `<path d="M96 120 C96 100 86 90 74 86 C78 96 84 106 84 120 Z" fill="${hex(C.goldDeep)}"/>` +
      `<path d="M8 120 C8 102 18 92 28 88 C26 98 22 108 22 120 Z" fill="${hex(l.metal)}" opacity="0.8"/>` +
      `<path d="M92 120 C92 102 82 92 72 88 C74 98 78 108 78 120 Z" fill="${hex(l.metal)}" opacity="0.8"/>` +
      `<path d="M50 96 L56 104 L50 116 L44 104 Z" fill="${hex(l.accent)}"/>`,
    /**
     * The scar. Old, badly closed, and across the good side so the light finds
     * it.
     *
     * It used to run from above the brow to below the nose, dead vertical and
     * dead straight, which at 230px is not a scar on a man — it is a line on a
     * picture. It now starts at the outer brow, crosses the cheekbone at an
     * angle and stops short of the jaw.
     */
    over: (_l, f) =>
      `<path d="M${50 + f.cheek * 0.78} ${f.eyeY - f.browY * 0.7 - 1} L${50 + f.cheek * 0.44} ${f.noseY + 1}" ` +
      `stroke="${hex(0x9a6a58)}" stroke-width="1.0" stroke-linecap="round" opacity="0.55"/>` +
      `<path d="M${50 + f.cheek * 0.6} ${f.eyeY - 4} L${50 + f.cheek * 0.72} ${f.eyeY - 3.4}" ` +
      `stroke="${hex(0x8f5a4a)}" stroke-width="0.8" stroke-linecap="round" opacity="0.7"/>` +
      `<path d="M${50 + f.cheek * 0.52} ${f.eyeY + 1} L${50 + f.cheek * 0.64} ${f.eyeY + 1.6}" ` +
      `stroke="${hex(0x8f5a4a)}" stroke-width="0.8" stroke-linecap="round" opacity="0.7"/>`,
  },

  /**
   * Has never lost a siege, which she attributes to arithmetic.
   *
   * The smooth one. A mail coif closes the whole silhouette into a dome and an
   * iron circlet cuts one hard horizontal across it — no loose hair anywhere,
   * because she is the only one of the five who has thought about what a helmet
   * does to a head.
   */
  castellan: {
    accent: C.gold,
    hair: 0x5e4227,
    hairLight: 0x8d6838,
    eye: 0x5f8672,
    cloth: 0x383b40,
    clothLight: 0x565b62,
    metal: 0x9aa1a8,
    skin: { base: 0xd8bfa4, shade: 0xb2957c, deep: 0x8a705c },
    gaze: 'open',
    crop: 1.22,
    cropDy: 1,
    /** The coif: one closed arc from shoulder to shoulder, over everything. */
    behind: (_l, f) =>
      `<path d="M${50 - f.skull - 4.5} ${f.chinY + 8} C${50 - f.skull - 5.5} ${f.crown - 8} ${50 - f.skull * 0.6} ${f.crown - 11} 50 ${f.crown - 11} ` +
      `C${50 + f.skull * 0.6} ${f.crown - 11} ${50 + f.skull + 5.5} ${f.crown - 8} ${50 + f.skull + 4.5} ${f.chinY + 8} ` +
      `L${50 + f.skull + 7} ${f.chinY + 16} L${50 - f.skull - 7} ${f.chinY + 16} Z" fill="${hex(0x5a5f66)}"/>` +
      // Mail, as a value break rather than as rings: rings at this size are noise.
      `<path d="M${50 - f.skull - 4.5} ${f.chinY + 8} C${50 - f.skull - 5.5} ${f.crown - 8} ${50 - f.skull * 0.6} ${f.crown - 11} 50 ${f.crown - 11} ` +
      `C${50 + f.skull * 0.2} ${f.crown - 11} ${50 + f.skull * 0.5} ${f.crown - 9} ${50 + f.skull * 0.5} ${f.crown - 6} ` +
      `C${50 - f.skull * 0.2} ${f.crown - 4} ${50 - f.skull - 1} ${f.cheekY} ${50 - f.skull - 1.5} ${f.chinY + 8} Z" ` +
      `fill="#000" opacity="0.3"/>` +
      // The face opening: the coif's own shadow around the jaw and temples.
      `<path d="M${50 - f.skull - 0.5} ${f.cheekY - 2} C${50 - f.skull - 0.5} ${f.jawY + 4} ${50 - f.chinW * 2} ${f.chinY + 3} 50 ${f.chinY + 3} ` +
      `C${50 + f.chinW * 2} ${f.chinY + 3} ${50 + f.skull + 0.5} ${f.jawY + 4} ${50 + f.skull + 0.5} ${f.cheekY - 2} ` +
      `C${50 + f.skull + 0.5} ${f.crown - 3} ${50 + f.skull * 0.6} ${f.crown - 6} 50 ${f.crown - 6} ` +
      `C${50 - f.skull * 0.6} ${f.crown - 6} ${50 - f.skull - 0.5} ${f.crown - 3} ${50 - f.skull - 0.5} ${f.cheekY - 2} Z" ` +
      `fill="${hex(0x494e55)}"/>`,
    hairBack: () => '',
    /** A straight-cut fringe under the circlet. Nothing hangs; nothing catches. */
    hairFront: (l, f) =>
      `<path d="M${50 - f.skull + 1} ${f.eyeY - f.browY - 2.5} C${50 - f.skull + 0.5} ${f.crown - 3} ${50 - f.skull * 0.5} ${f.crown - 5} 50 ${f.crown - 5} ` +
      `C${50 + f.skull * 0.5} ${f.crown - 5} ${50 + f.skull - 0.5} ${f.crown - 3} ${50 + f.skull - 1} ${f.eyeY - f.browY - 2.5} ` +
      `C${50 + f.skull * 0.62} ${f.eyeY - f.browY - 5.5} ${50 + 4} ${f.eyeY - f.browY - 4.5} ${50 + 1.5} ${f.eyeY - f.browY - 6.5} ` +
      `C${50 - 4} ${f.eyeY - f.browY - 4} ${50 - f.skull * 0.62} ${f.eyeY - f.browY - 5.5} ${50 - f.skull + 1} ${f.eyeY - f.browY - 2.5} Z" ` +
      `fill="${hex(l.hair)}"/>` +
      `<path d="M${50 + 1.5} ${f.eyeY - f.browY - 6.5} C${50 + 5} ${f.crown - 3} ${50 + f.skull * 0.7} ${f.crown - 3} ${50 + f.skull - 1} ${f.eyeY - f.browY - 2.5} ` +
      `C${50 + f.skull * 0.6} ${f.eyeY - f.browY - 6} ${50 + 5} ${f.eyeY - f.browY - 5} ${50 + 1.5} ${f.eyeY - f.browY - 6.5} Z" ` +
      `fill="${hex(l.hairLight)}" opacity="0.55"/>` +
      // The circlet, the one hard horizontal on the whole head.
      `<path d="M${50 - f.skull - 1.5} ${f.eyeY - f.browY - 10.5} C${50 - f.skull * 0.5} ${f.eyeY - f.browY - 13.5} ${50 + f.skull * 0.5} ${f.eyeY - f.browY - 13.5} ${50 + f.skull + 1.5} ${f.eyeY - f.browY - 10.5} ` +
      `L${50 + f.skull + 1.5} ${f.eyeY - f.browY - 8.2} C${50 + f.skull * 0.5} ${f.eyeY - f.browY - 11.2} ${50 - f.skull * 0.5} ${f.eyeY - f.browY - 11.2} ${50 - f.skull - 1.5} ${f.eyeY - f.browY - 8.2} Z" ` +
      `fill="${hex(l.accent)}"/>` +
      `<path d="M50 ${f.eyeY - f.browY - 15.5} L${50 + 2.6} ${f.eyeY - f.browY - 11.8} L50 ${f.eyeY - f.browY - 8.6} L${50 - 2.6} ${f.eyeY - f.browY - 11.8} Z" ` +
      `fill="${hex(C.goldPale)}"/>`,
    body: (l) =>
      `<path d="M14 120 C14 100 28 88 50 88 C72 88 86 100 86 120 Z" fill="${hex(l.cloth)}"/>` +
      // A surcoat, and the only saturated thing on her.
      `<path d="M50 86 L62 96 L50 120 L38 96 Z" fill="${hex(C.bone)}" opacity="0.9"/>` +
      `<path d="M50 94 L55 104 L50 116 L45 104 Z" fill="${hex(l.accent)}"/>` +
      `<path d="M18 120 C18 102 28 92 40 88 L44 95 C34 99 28 108 28 120 Z" fill="${hex(l.metal)}"/>` +
      `<path d="M82 120 C82 102 72 92 60 88 L56 95 C66 99 72 108 72 120 Z" fill="${hex(l.metal)}"/>` +
      `<path d="M18 120 C18 102 28 92 40 88 L41 91 C31 96 24 106 24 120 Z" fill="${hex(C.boneDim)}" opacity="0.5"/>`,
  },

  /**
   * Counts the enemy by smell and is seldom wrong.
   *
   * The hood came off. It read as horns — two dark points either side of the
   * skull that the eye resolves as a silhouette before it resolves as cloth —
   * and no amount of shading fixed it. What replaced it is a wolf pelt worn
   * across the shoulders with the skull still in it, which gives the same
   * ragged mass low down where it cannot be mistaken for a head.
   */
  huntmaster: {
    accent: C.plagueLight,
    hair: 0x3a3228,
    hairLight: 0x5c5140,
    brow: 0x332b24,
    eye: 0x8a9a48,
    cloth: 0x2f3a2c,
    clothLight: 0x44523c,
    metal: 0x6b6350,
    skin: { base: 0xb99878, shade: 0x8f7157, deep: 0x634835 },
    gaze: 'narrow',
    crop: 1.16,
    cropDy: 0,
    /** The pelt, behind everything, running off both edges. */
    beneath: (l) =>
      `<path d="M2 120 C2 96 16 82 34 80 C40 86 60 86 66 80 C84 82 98 96 98 120 Z" fill="${hex(0x4a4034)}"/>` +
      `<path d="M2 120 C2 100 12 88 24 84 C22 96 18 108 18 120 Z" fill="${hex(0x2e281f)}" opacity="0.7"/>` +
      `<path d="M98 120 C98 100 88 88 76 84 C78 96 82 108 82 120 Z" fill="${hex(0x2e281f)}" opacity="0.7"/>` +
      // Fur, as five wedges rather than as hair: five reads, fifty is grey.
      `<path d="M10 96 L16 108 L22 94 L28 106 L34 92 L38 104 L44 90 L44 120 L8 120 Z" fill="${hex(0x5c5040)}" opacity="0.55"/>` +
      `<path d="M90 96 L84 108 L78 94 L72 106 L66 92 L62 104 L56 90 L56 120 L92 120 Z" fill="${hex(0x5c5040)}" opacity="0.4"/>` +
      `<path d="M0 0 L0 0 Z" fill="${hex(l.cloth)}"/>`,
    /**
     * Long, and falling behind the shoulders rather than around the face.
     *
     * The first curtain bulged five units past the temple on each side and swept
     * its inner edge right across the crown, so the head sat in an enclosing
     * dark mass — which at thumbnail size is a hood, and a hood is the one thing
     * this character is not allowed to have. It now hugs the skull at the top
     * and hangs as two narrow falls, and the ear is left showing on the lit
     * side, because an ear is the cheapest proof that nothing is covering it.
     */
    hairBack: (l, f) =>
      `<path d="M${50 - f.skull - 1.5} ${f.chinY + 14} C${50 - f.skull - 2.5} ${f.crown + 6} ${50 - f.skull * 0.6} ${f.crown - 7} 50 ${f.crown - 7} ` +
      `C${50 + f.skull * 0.6} ${f.crown - 7} ${50 + f.skull + 2.5} ${f.crown + 6} ${50 + f.skull + 1.5} ${f.chinY + 14} ` +
      `L${50 + f.skull - 2.5} ${f.chinY + 14} ` +
      `C${50 + f.skull - 1.5} ${f.jawY} ${50 + f.skull * 0.5} ${f.crown + 1} 50 ${f.crown + 1} ` +
      `C${50 - f.skull * 0.5} ${f.crown + 1} ${50 - f.skull + 1.5} ${f.jawY} ${50 - f.skull + 2.5} ${f.chinY + 14} Z" fill="${hex(l.hair)}"/>` +
      // One lock over the far shoulder, and they are what make him look unbarbered.
      `<path d="M${50 - f.skull - 1} ${f.cheekY - 4} L${50 - f.skull - 4} ${f.chinY + 12} L${50 - f.skull} ${f.chinY + 11} Z" fill="${hex(l.hair)}"/>`,
    /**
     * The fringe, swept. The previous cut was a zigzag of four sharp points
     * across the forehead, and the three dark wedges left standing between them
     * read as horns — which is the exact complaint the hood was removed for, so
     * removing the hood had fixed nothing. Points read as points at any size.
     * What replaced it is one sweep, low on the far side and high on the near,
     * with a single lock broken out of it.
     */
    hairFront: (l, f) =>
      `<path d="M${50 - f.skull - 1} ${f.eyeY - f.browY + 1} C${50 - f.skull - 1.5} ${f.crown - 4} ${50 - f.skull * 0.5} ${f.crown - 7} 50 ${f.crown - 7} ` +
      `C${50 + f.skull * 0.5} ${f.crown - 7} ${50 + f.skull + 1.5} ${f.crown - 4} ${50 + f.skull + 1} ${f.eyeY - f.browY - 3} ` +
      `C${50 + f.skull * 0.5} ${f.eyeY - f.browY - 5.5} ${50 - f.skull * 0.2} ${f.eyeY - f.browY - 2} ${50 - f.skull - 1} ${f.eyeY - f.browY + 1} Z" ` +
      `fill="${hex(l.hair)}"/>` +
      // One lock, down past the far brow. Asymmetry, and nothing else, is what
      // keeps a swept fringe from reading as a helmet of hair.
      `<path d="M${50 - f.skull * 0.9} ${f.eyeY - f.browY - 2} C${50 - f.skull * 0.5} ${f.eyeY - f.browY + 3} ${50 - f.skull * 0.2} ${f.eyeY - 1} ${50 - f.skull * 0.45} ${f.eyeY + 2} ` +
      `C${50 - f.skull * 0.55} ${f.eyeY - 2} ${50 - f.skull * 0.8} ${f.eyeY - f.browY + 2} ${50 - f.skull * 1.05} ${f.eyeY - f.browY - 1} Z" ` +
      `fill="${hex(l.hair)}"/>` +
      `<path d="M${50 - 1} ${f.eyeY - f.browY - 4} C${50 + 5} ${f.crown - 5} ${50 + f.skull * 0.8} ${f.crown - 3} ${50 + f.skull} ${f.eyeY - f.browY - 3.5} ` +
      `C${50 + f.skull * 0.5} ${f.eyeY - f.browY - 5.5} ${50 + 2} ${f.eyeY - f.browY - 4.5} ${50 - 1} ${f.eyeY - f.browY - 4} Z" ` +
      `fill="${hex(l.hairLight)}" opacity="0.4"/>`,
    body: (l, f) =>
      `<path d="M20 120 C20 102 32 90 50 90 C68 90 80 102 80 120 Z" fill="${hex(l.cloth)}"/>` +
      // The wolf's skull, over the left shoulder. It is the thing you remember.
      `<path d="M14 96 C20 88 32 86 38 92 C40 98 36 106 28 108 C20 110 12 104 14 96 Z" fill="${hex(C.boneDim)}"/>` +
      `<path d="M14 96 C20 88 32 86 38 92 C34 92 24 94 20 100 C17 104 16 108 18 110 C13 108 12 101 14 96 Z" fill="${hex(C.bone)}"/>` +
      `<path d="M22 100 L16 108 L24 106 Z" fill="${hex(0x1a1713)}"/>` +
      `<circle cx="26" cy="96" r="2.1" fill="${hex(0x14110e)}"/>` +
      `<path d="M17 91 L14 85 L21 89 Z" fill="${hex(C.boneDim)}"/>` +
      `<path d="M31 88 L30 82 L36 88 Z" fill="${hex(C.boneDim)}"/>` +
      // A quiver strap across the chest, in the one colour he is allowed.
      `<path d="M60 88 L70 92 L44 120 L34 118 Z" fill="${hex(l.clothLight)}"/>` +
      `<path d="M62 90 L66 91.6 L42 119 L38 118 Z" fill="${hex(l.accent)}" opacity="0.55"/>` +
      // Arrow nocks over the far shoulder, which says archer without a bow.
      `<path d="M70 78 L72 90 M74 77 L76 90 M78 79 L79 91" stroke="${hex(0x6a5c44)}" stroke-width="1.6" stroke-linecap="round"/>` +
      `<path d="M68 76 L74 74 L72 80 Z" fill="${hex(C.boneDim)}"/>` +
      `<path d="M72 75 L78 73 L76 79 Z" fill="${hex(C.boneDim)}"/>` +
      `<path d="M${50 - f.neck * 2} ${f.chinY + 10} C${50 - f.neck} ${f.chinY + 16} ${50 + f.neck} ${f.chinY + 16} ${50 + f.neck * 2} ${f.chinY + 10} ` +
      `L${50 + f.neck * 2.6} ${f.chinY + 17} L${50 - f.neck * 2.6} ${f.chinY + 17} Z" fill="${hex(0x594c3c)}"/>`,
    /**
     * Three claw marks, healed white. Something got a paw to him and did not
     * survive the disagreement.
     *
     * They used to run from the temple to below the nose, straight, pale and
     * ruler-perfect, across both eyes — three lines that long over a face read
     * as a crack in the glass rather than as scars in a man. They now start on
     * the cheekbone, stop at the jaw, miss the eye, and are half the opacity.
     */
    over: (_l, f) =>
      `<path d="M${50 - f.cheek * 0.86} ${f.eyeY + 2.5} L${50 - f.cheek * 0.5} ${f.noseY + 4}" stroke="${hex(0xcbb49a)}" stroke-width="0.8" stroke-linecap="round" opacity="0.4"/>` +
      `<path d="M${50 - f.cheek * 0.72} ${f.eyeY + 1.6} L${50 - f.cheek * 0.36} ${f.noseY + 3.4}" stroke="${hex(0xcbb49a)}" stroke-width="0.8" stroke-linecap="round" opacity="0.34"/>` +
      `<path d="M${50 - f.cheek * 0.58} ${f.eyeY + 1.2} L${50 - f.cheek * 0.24} ${f.noseY + 2.6}" stroke="${hex(0xcbb49a)}" stroke-width="0.7" stroke-linecap="round" opacity="0.28"/>`,
  },

  /**
   * Keeps a tower, and a ledger of debts owed to things without names.
   *
   * The narrow one. Nothing on the head at all — the silhouette is made below
   * it, by a collar that rises past his ears in two hard spikes, so at thumbnail
   * size he is a thin skull between two blades. Lit from inside, which is the
   * tell.
   */
  sorcerer: {
    accent: C.indigoLight,
    hair: 0xc4bbaa,
    hairLight: 0xeae3d4,
    brow: 0x8e8674,
    eye: 0x7fa8d4,
    cloth: 0x22214a,
    clothLight: 0x3b3a72,
    metal: C.will,
    skin: { base: 0xd4ccc0, shade: 0xaba394, deep: 0x7f766a },
    gaze: 'open',
    crop: 1.08,
    cropDy: 3,
    hairBack: (l, f) =>
      `<path d="M${50 - f.skull - 1} ${f.cheekY + 4} C${50 - f.skull - 2} ${f.crown - 3} ${50 - f.skull * 0.5} ${f.crown - 5} 50 ${f.crown - 5} ` +
      `C${50 + f.skull * 0.5} ${f.crown - 5} ${50 + f.skull + 2} ${f.crown - 3} ${50 + f.skull + 1} ${f.cheekY + 4} ` +
      `L${50 + f.skull + 4} ${f.chinY + 14} L${50 + f.skull - 2} ${f.chinY + 6} ` +
      `L${50 - f.skull + 2} ${f.chinY + 6} L${50 - f.skull - 4} ${f.chinY + 14} Z" fill="${hex(l.hair)}"/>`,
    /** Thin, swept straight back off a high forehead. It is going, and he has not noticed. */
    hairFront: (l, f) =>
      `<path d="M${50 - f.skull} ${f.eyeY - f.browY - 5} C${50 - f.skull - 1} ${f.crown - 2} ${50 - f.skull * 0.5} ${f.crown - 4} 50 ${f.crown - 4} ` +
      `C${50 + f.skull * 0.5} ${f.crown - 4} ${50 + f.skull + 1} ${f.crown - 2} ${50 + f.skull} ${f.eyeY - f.browY - 5} ` +
      `C${50 + f.skull * 0.6} ${f.eyeY - f.browY - 9} ${50 + f.skull * 0.3} ${f.crown - 1} 50 ${f.crown - 1.5} ` +
      `C${50 - f.skull * 0.3} ${f.crown - 1} ${50 - f.skull * 0.6} ${f.eyeY - f.browY - 9} ${50 - f.skull} ${f.eyeY - f.browY - 5} Z" ` +
      `fill="${hex(l.hair)}"/>` +
      `<path d="M${50 - f.skull * 0.8} ${f.eyeY - f.browY - 6} C${50 - f.skull * 0.3} ${f.crown - 3.4} ${50 + f.skull * 0.3} ${f.crown - 3.4} ${50 + f.skull * 0.8} ${f.eyeY - f.browY - 6} ` +
      `C${50 + f.skull * 0.3} ${f.crown - 1.8} ${50 - f.skull * 0.3} ${f.crown - 1.8} ${50 - f.skull * 0.8} ${f.eyeY - f.browY - 6} Z" ` +
      `fill="${hex(l.hairLight)}" opacity="0.6"/>` +
      // Hollow cheeks. Two strokes, and they do more than the whole head shape.
      `<path d="M${50 - f.cheek + 1.5} ${f.cheekY + 2} C${50 - f.cheek + 2} ${f.jawY - 2} ${50 - f.jaw - 1} ${f.jawY + 2} ${50 - f.jaw - 1.5} ${f.jawY + 4}" ` +
      `stroke="${hex(0x7f766a)}" stroke-width="1.1" fill="none" opacity="0.4"/>` +
      `<path d="M${50 + f.cheek - 1.5} ${f.cheekY + 2} C${50 + f.cheek - 2} ${f.jawY - 2} ${50 + f.jaw + 1} ${f.jawY + 2} ${50 + f.jaw + 1.5} ${f.jawY + 4}" ` +
      `stroke="${hex(0x7f766a)}" stroke-width="1.1" fill="none" opacity="0.32"/>`,
    body: (l, f) =>
      `<path d="M22 120 C22 102 34 92 50 92 C66 92 78 102 78 120 Z" fill="${hex(l.cloth)}"/>` +
      // The collar. Two spikes past the ears, and they are the silhouette.
      `<path d="M50 94 L${50 - f.skull - 4} ${f.chinY + 4} L${50 - f.skull - 9} ${f.cheekY - 4} ` +
      `L${50 - f.skull - 3} ${f.jawY} L38 100 Z" fill="${hex(l.clothLight)}"/>` +
      `<path d="M50 94 L${50 + f.skull + 4} ${f.chinY + 4} L${50 + f.skull + 9} ${f.cheekY - 4} ` +
      `L${50 + f.skull + 3} ${f.jawY} L62 100 Z" fill="${hex(l.clothLight)}"/>` +
      `<path d="M${50 - f.skull - 9} ${f.cheekY - 4} L${50 - f.skull - 5.5} ${f.cheekY + 6} L${50 - f.skull - 3} ${f.jawY} Z" fill="${hex(l.accent)}" opacity="0.8"/>` +
      `<path d="M${50 + f.skull + 9} ${f.cheekY - 4} L${50 + f.skull + 5.5} ${f.cheekY + 6} L${50 + f.skull + 3} ${f.jawY} Z" fill="${hex(l.accent)}" opacity="0.8"/>` +
      `<path d="M40 100 C44 96 56 96 60 100 C60 106 56 110 50 110 C44 110 40 106 40 100 Z" fill="${hex(l.cloth)}"/>` +
      `<circle cx="50" cy="103" r="4" fill="${hex(l.metal)}"/>` +
      `<circle cx="50" cy="103" r="1.8" fill="#fff" opacity="0.8"/>`,
    /** Lit from inside. The one thing on any of these five that is not anatomy. */
    over: (l, f) =>
      `<ellipse cx="${50 - f.eyeDx}" cy="${f.eyeY}" rx="${f.eyeW * 1.25}" ry="${f.eyeH * 1.3}" fill="${hex(l.eye)}" opacity="0.16"/>` +
      `<ellipse cx="${50 + f.eyeDx}" cy="${f.eyeY}" rx="${f.eyeW * 1.25}" ry="${f.eyeH * 1.3}" fill="${hex(l.eye)}" opacity="0.16"/>` +
      `<path d="M50 ${f.crown - 8} L${50 + 1.4} ${f.crown - 4.5} L50 ${f.crown - 1} L${50 - 1.4} ${f.crown - 4.5} Z" ` +
      `fill="${hex(l.metal)}" opacity="0.85"/>`,
  },

  /**
   * Carries the ninth bell's clapper in a satchel and will not explain.
   *
   * The tall one, and the blind one. The mitre gives him a silhouette nobody
   * else in the game has, and the bound cloth over the eyes does the work that
   * a pair of closed lids could not: at forty pixels, closed lids are an absence
   * and a blindfold is a fact. Old, and heavy with it — this is the only face
   * here with jowls, forehead lines and a mouth that has given up.
   */
  prelate: {
    accent: C.goldPale,
    hair: 0x9a9186,
    hairLight: 0xb8b0a2,
    brow: 0x9c9284,
    eye: 0xc8a44c,
    cloth: 0xc9bda2,
    clothLight: 0xe6dcc8,
    metal: C.gold,
    skin: { base: 0xcbb59a, shade: 0xa38d74, deep: 0x776450 },
    gaze: 'blind',
    crop: 1.00,
    cropDy: 9,
    /**
     * The lappets, and only the lappets.
     *
     * The mitre itself is worn *on* the head, so it is drawn in `hairFront`,
     * over the face — see `mitre()`. Rendered here, behind everything, the skull
     * covered it from the hairline down and all that showed above was the peak
     * sitting on a gold bar: a pagoda roof on a lintel, which is exactly what
     * the 230px sheet showed.
     */
    behind: (l, f) => {
      const base = f.crown - 1;
      return (
        `<path d="M${50 - f.skull - 1} ${base + 2} L${50 - f.skull - 3.5} ${f.chinY + 16} L${50 - f.skull + 1.5} ${f.chinY + 16} L${50 - f.skull + 2} ${base + 2} Z" ` +
        `fill="${hex(l.cloth)}" opacity="0.85"/>` +
        `<path d="M${50 + f.skull + 1} ${base + 2} L${50 + f.skull + 3.5} ${f.chinY + 16} L${50 + f.skull - 1.5} ${f.chinY + 16} L${50 + f.skull - 2} ${base + 2} Z" ` +
        `fill="${hex(l.clothLight)}" opacity="0.85"/>` +
        `<path d="M${50 - f.skull - 3.2} ${f.chinY + 12} L${50 - f.skull + 1.4} ${f.chinY + 12} L${50 - f.skull + 1.4} ${f.chinY + 14.5} L${50 - f.skull - 3.2} ${f.chinY + 14.5} Z" fill="${hex(l.metal)}" opacity="0.8"/>` +
        `<path d="M${50 + f.skull + 3.2} ${f.chinY + 12} L${50 + f.skull - 1.4} ${f.chinY + 12} L${50 + f.skull - 1.4} ${f.chinY + 14.5} L${50 + f.skull + 3.2} ${f.chinY + 14.5} Z" fill="${hex(l.metal)}" opacity="0.8"/>`
      );
    },
    hairBack: () => '',
    /**
     * The mitre, then what is left of his hair under it, then a beard that has
     * not been cut in years.
     */
    hairFront: (l, f) =>
      mitre(l, f) +
      `<path d="M${50 - f.skull - 1} ${f.cheekY - 2} C${50 - f.skull - 1} ${f.crown - 2} ${50 - f.skull * 0.5} ${f.crown - 3} ${50 - f.skull * 0.3} ${f.crown - 2} ` +
      `L${50 - f.skull * 0.4} ${f.eyeY - f.browY - 6} C${50 - f.skull * 0.75} ${f.eyeY - f.browY - 4} ${50 - f.skull} ${f.cheekY - 8} ${50 - f.skull - 1} ${f.cheekY - 2} Z" fill="${hex(l.hair)}"/>` +
      `<path d="M${50 + f.skull + 1} ${f.cheekY - 2} C${50 + f.skull + 1} ${f.crown - 2} ${50 + f.skull * 0.5} ${f.crown - 3} ${50 + f.skull * 0.3} ${f.crown - 2} ` +
      `L${50 + f.skull * 0.4} ${f.eyeY - f.browY - 6} C${50 + f.skull * 0.75} ${f.eyeY - f.browY - 4} ${50 + f.skull} ${f.cheekY - 8} ${50 + f.skull + 1} ${f.cheekY - 2} Z" fill="${hex(l.hair)}"/>` +
      `<path d="M${50 - f.jaw - 1} ${f.jawY - 6} C${50 - f.jaw - 2} ${f.chinY + 6} ${50 - 8} ${f.chinY + 18} 50 ${f.chinY + 20} ` +
      `C${50 + 8} ${f.chinY + 18} ${50 + f.jaw + 2} ${f.chinY + 6} ${50 + f.jaw + 1} ${f.jawY - 6} ` +
      `C${50 + f.jaw} ${f.jawY + 4} ${50 + f.chinW} ${f.mouthY + 1} 50 ${f.mouthY + 2} ` +
      `C${50 - f.chinW} ${f.mouthY + 1} ${50 - f.jaw} ${f.jawY + 4} ${50 - f.jaw - 1} ${f.jawY - 6} Z" fill="${hex(l.hair)}"/>` +
      `<path d="M50 ${f.mouthY + 2} C${50 + f.chinW} ${f.mouthY + 1} ${50 + f.jaw} ${f.jawY + 4} ${50 + f.jaw + 1} ${f.jawY - 6} ` +
      `C${50 + f.jaw + 2} ${f.chinY + 6} ${50 + 8} ${f.chinY + 18} 50 ${f.chinY + 20} ` +
      `C${50 + 3} ${f.chinY + 12} ${50 + 4} ${f.chinY} ${50 + 2} ${f.mouthY + 4} Z" fill="${hex(l.hairLight)}" opacity="0.5"/>` +
      // Moustache, over the mouth. He is not going to smile, so the mouth can go.
      `<path d="M${50 - f.mouthW - 3} ${f.mouthY - 2.5} C${50 - 2} ${f.mouthY - 4.5} ${50 + 2} ${f.mouthY - 4.5} ${50 + f.mouthW + 3} ${f.mouthY - 2.5} ` +
      `C${50 + f.mouthW + 1} ${f.mouthY + 2} ${50 - f.mouthW - 1} ${f.mouthY + 2} ${50 - f.mouthW - 3} ${f.mouthY - 2.5} Z" fill="${hex(l.hair)}"/>`,
    body: (l) =>
      `<path d="M12 120 C12 98 28 88 50 88 C72 88 88 98 88 120 Z" fill="${hex(0x38332c)}"/>` +
      // The pallium: two white falls either side of a black chasuble.
      `<path d="M42 88 C40 102 36 114 36 120 L20 120 C20 104 28 92 34 89 Z" fill="${hex(l.cloth)}"/>` +
      `<path d="M58 88 C60 102 64 114 64 120 L80 120 C80 104 72 92 66 89 Z" fill="${hex(l.cloth)}"/>` +
      `<path d="M42 88 C40 102 36 114 36 120 L30 120 C31 106 35 94 38 88.6 Z" fill="${hex(C.boneDim)}" opacity="0.45"/>` +
      `<path d="M34 108 L44 108 L44 112 L34 112 Z" fill="${hex(l.metal)}" opacity="0.75"/>` +
      `<path d="M56 108 L66 108 L66 112 L56 112 Z" fill="${hex(l.metal)}" opacity="0.75"/>` +
      // The ninth bell's clapper, on a chain. He will not explain.
      `<path d="M50 92 L50 104" stroke="${hex(l.metal)}" stroke-width="1.8"/>` +
      `<path d="M50 104 m -5.5 0 a 5.5 6.5 0 1 0 11 0 a 5.5 6.5 0 1 0 -11 0" fill="${hex(l.metal)}"/>` +
      `<path d="M50 104 m -5.5 0 a 5.5 6.5 0 0 1 5.5 -6.5 L50 110.5 Z" fill="${hex(C.goldPale)}" opacity="0.5"/>` +
      `<circle cx="50" cy="112" r="1.8" fill="${hex(C.goldDeep)}"/>`,
  },
};

/* ── Assembly ────────────────────────────────────────────────────────────── */

export interface PortraitOptions {
  /** Pixel size of the square. Defaults to 96. */
  size?: number;
  /** Draw the gilded arch. Off for tiny inline uses. */
  frame?: boolean;
  /** Dim everything but the silhouette — for a locked or unchosen character. */
  muted?: boolean;
}

/** The niche and arch, shared by heroes and troop cards alike. */
const ARCH = 'M6 120 L6 52 C6 24 26 6 50 6 C74 6 94 24 94 52 L94 120 Z';

/**
 * One portrait, as an SVG string ready for `innerHTML`.
 *
 * The construction is fixed and only `LOOKS`/`SHAPE` vary: niche, backlight,
 * pelt or mantle, neck, then the head group — hood, hair behind, face, eyes,
 * features, hair in front — then the body, then the light, then the frame.
 *
 * That order is the whole trick. Hair in front of the face and behind it are
 * different shapes and a bust with only one of them reads flat; the neck goes
 * before the head or it fills over the jaw; and the light goes over everything
 * or it is a colour rather than a light.
 */
export function heroPortrait(id: HeroArchetype, opts: PortraitOptions = {}): string {
  const l = LOOKS[id];
  const f = SHAPE[id];
  if (!l || !f) return '';
  const size = opts.size ?? 96;
  const frame = opts.frame !== false;
  const uid = `hp-${id}`;
  // The head turns; the shoulders do not. Rotating about the base of the neck is
  // what makes a tilt read as bearing instead of as a mistake.
  const pivot = `${50} ${f.chinY + 8}`;

  return `
<svg viewBox="0 0 100 120" width="${size}" height="${size * 1.2}" role="img"
     aria-label="${id} portrait" style="display:block${opts.muted ? ';filter:grayscale(0.55) brightness(0.8)' : ''}">
  <defs>
    <radialGradient id="${uid}-glow" cx="50%" cy="36%" r="62%">
      <stop offset="0%" stop-color="${hex(l.accent)}" stop-opacity="0.5"/>
      <stop offset="55%" stop-color="${hex(l.accent)}" stop-opacity="0.12"/>
      <stop offset="100%" stop-color="${hex(C.ashDeep)}" stop-opacity="0"/>
    </radialGradient>
    <linearGradient id="${uid}-key" x1="0" y1="0" x2="1" y2="0.35">
      <stop offset="0%" stop-color="#000" stop-opacity="0.52"/>
      <stop offset="42%" stop-color="#000" stop-opacity="0.16"/>
      <stop offset="72%" stop-color="#000" stop-opacity="0"/>
      <stop offset="100%" stop-color="${hex(C.goldPale)}" stop-opacity="0.18"/>
    </linearGradient>
    <linearGradient id="${uid}-sink" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${hex(C.ashDeep)}" stop-opacity="0"/>
      <stop offset="62%" stop-color="${hex(C.ashDeep)}" stop-opacity="0.1"/>
      <stop offset="100%" stop-color="${hex(C.ashDeep)}" stop-opacity="0.82"/>
    </linearGradient>
    <linearGradient id="${uid}-niche" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${hex(C.ashLight)}"/>
      <stop offset="100%" stop-color="${hex(C.ashDeep)}"/>
    </linearGradient>
    <clipPath id="${uid}-clip"><path d="${ARCH}"/></clipPath>
  </defs>

  <path d="${ARCH}" fill="url(#${uid}-niche)"/>
  <g clip-path="url(#${uid}-clip)">
    <rect x="0" y="0" width="100" height="120" fill="url(#${uid}-glow)"/>
    <!--
      The crop is the composition.

      Drawn at 1:1 the head occupies a third of the niche and the portrait reads
      as a figure standing in a doorway — distant, and with eyes too small to
      carry an expression. Portraits of this kind crop tight: the head takes
      about half the height, the shoulders run off the bottom edge, and the
      frame cuts the hair rather than containing it. Scaling about the face
      rather than the origin keeps every part in the same relationship while
      pushing the shoulders out of frame.

      The scale is per-character by a few hundredths, and only because a mitre
      and a bare head do not need the same headroom. The *kind* of crop is the
      constant; the exact number is not worth being precious about.
    -->
    <g transform="translate(50 ${46 + l.cropDy}) scale(${l.crop}) translate(-50 -55)">
      ${l.beneath ? l.beneath(l, f) : ''}
      ${neck(f, l.skin)}
      <g transform="rotate(${f.tilt} ${pivot})">
        ${l.behind ? l.behind(l, f) : ''}
        ${l.hairBack(l, f)}
        ${ears(f, l.skin)}
        ${modelling(f, l.skin, uid)}
        ${eyes(f, l, l.skin, uid)}
        ${features(f, l.skin)}
        ${l.hairFront(l, f)}
        ${l.over ? l.over(l, f) : ''}
      </g>
      ${l.body(l, f)}
    </g>
    <!--
      The light is doing the work the geometry cannot.

      Flatly lit, these read as dolls: an even beige face with features placed
      on it. A single key from the right, a shadow across the left cheek and a
      pale rim down the lit edge give the head volume it does not actually
      have — and the sink at the bottom drops the shoulders into the dark
      instead of ending them on a hard edge, which is what the niche is for.
    -->
    <rect x="0" y="0" width="100" height="120" fill="url(#${uid}-key)"/>
    <rect x="0" y="0" width="100" height="120" fill="url(#${uid}-sink)"/>
    <rect x="0" y="0" width="100" height="120" fill="url(#${uid}-glow)" opacity="0.28"
          style="mix-blend-mode:screen"/>
  </g>
  ${
    frame
      ? `<path d="M6 120 L6 52 C6 24 26 6 50 6 C74 6 94 24 94 52 L94 120"
                fill="none" stroke="${hex(C.goldDeep)}" stroke-width="3"/>
         <path d="M8.5 120 L8.5 52 C8.5 25.5 27 8.5 50 8.5 C73 8.5 91.5 25.5 91.5 52 L91.5 120"
                fill="none" stroke="${hex(C.gold)}" stroke-width="1.2" opacity="0.85"/>`
      : ''
  }
</svg>`;
}

/** Every class id that has a portrait, for the picker. */
export const PORTRAIT_IDS = Object.keys(LOOKS) as HeroArchetype[];

/* ── Leaders ─────────────────────────────────────────────────────────────── */

/**
 * A leader portrait is the same construction as a hero's, driven from data.
 *
 * The five heroes are hand-authored: a `Shape` and a `Look` each, tuned by eye
 * over several renders. A leader cannot be, because there are twenty-two of them
 * across eleven peoples and the roster will grow. So the same parts — the same
 * head path, the same neck-before-head order, the same key light, the same arch
 * — are driven from `RaceFace` (the body, shared by every kingdom of a people)
 * and `HouseLook` (the culture, which is the only place two kingdoms of one
 * people are allowed to differ).
 *
 * That split is not a convenience. Two goblin kingdoms have identical bone by
 * construction, so if anything cultural leaked into `RaceFace` they would be one
 * kingdom in two colours — the exact thing the owner asked for by name. The
 * contract in `racefaces.ts` argues it at length.
 *
 * All of it lives in this file rather than a new one because it shares
 * `headPath`, `neck`, `modelling`, `eyes`, `features` and `ARCH` with the
 * heroes, and a second renderer over the same construction is how two sets of
 * art drift apart. If this file grows much further the right move is to lift
 * those shared parts into a `facekit.ts` that both import — never to copy them.
 */

/**
 * What a material does to light, which is nearly all a material is.
 *
 * The cheapest field in `HouseLook` by a distance: a colour and one number, not
 * a shape, so eleven materials cost about what one crown costs. `spec` is how
 * hard the highlight is — glass and lacquer throw a narrow bright one, rag and
 * stone throw none — and that difference survives at card size where the shape
 * of the ornament does not.
 */
const MATERIAL: Record<HouseLook['material'], { base: number; spec: number }> = {
  iron: { base: 0x6e6a63, spec: 0.16 },
  steel: { base: 0x9aa1a8, spec: 0.34 },
  gold: { base: 0xc8a44c, spec: 0.3 },
  bronze: { base: 0x9a7440, spec: 0.24 },
  bone: { base: 0xcfc6b0, spec: 0.1 },
  coral: { base: 0xb08a86, spec: 0.16 },
  lacquer: { base: 0x46302c, spec: 0.5 },
  rag: { base: 0x6b6153, spec: 0.03 },
  glass: { base: 0x8fa8ae, spec: 0.55 },
  wood: { base: 0x6a5238, spec: 0.07 },
  stone: { base: 0x7a7770, spec: 0.05 },
};

interface Kit {
  metal: number;
  lit: number;
  deep: number;
  cloth: number;
  clothLight: number;
  hair: number;
  hairLight: number;
  accent: number;
}

/** A stable number from a string. A hash, not a roll — same discipline as `troopart.ts`. */
function idHash(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

function kitOf(face: RaceFace, look: HouseLook): Kit {
  const m = MATERIAL[look.material] ?? MATERIAL.iron;
  const seed = idHash(look.id);
  const hair = face.hair.length > 0 ? (face.hair[Math.floor(seed * face.hair.length)] ?? face.hair[0]!) : 0x2b241d;
  // Cloth is the accent taken right down and mixed most of the way to ash. The
  // world is ash and bone: the accent gets the niche behind the figure and a
  // trim line or two in front of it, and the garment only ever gets a hint.
  const cloth = mix(shade(look.accent, 0.34), C.ashLight, 0.52);
  return {
    metal: m.base,
    lit: mix(m.base, 0xffffff, 0.18 + m.spec * 0.55),
    deep: shade(m.base, 0.52),
    cloth,
    clothLight: mix(cloth, 0xffffff, 0.17),
    hair,
    hairLight: mix(hair, 0xd8cbb0, 0.3),
    accent: look.accent,
  };
}

/**
 * `RaceFace` and `HouseLook` into the `Shape` the drawing code wants.
 *
 * Every geometric field in `RaceFace` is a multiplier where 1 is human, and this
 * function is the only place that knows what 1 means. That indirection is the
 * point: the numbers in `BASE` have been retuned four times already and a roster
 * written against them directly would have broken on every one.
 *
 * The eyeline is the anchor. Scaling about it rather than about the top of the
 * frame is what lets `cranium` and `faceLength` move independently — a big vault
 * over a short face and a small vault over a long one are two different heads,
 * and scaling the whole skull about its crown makes them one head at two sizes.
 */
function shapeOf(face: RaceFace, look: HouseLook): Shape {
  const eyeY = BASE.eyeY;
  const len = face.faceLength;
  const up = (v: number): number => eyeY - (eyeY - v) * face.cranium * (0.55 + 0.45 * len);
  const down = (v: number): number => eyeY + (v - eyeY) * len;
  return {
    crown: up(BASE.crown),
    skull: BASE.skull * face.temple,
    cheek: BASE.cheek * face.cheek,
    // 0 puts the cheekbone high and gaunt, 1 low and soft. Human is 0.5.
    cheekY: eyeY - 7 + face.cheekHeight * 8,
    jaw: BASE.jaw * face.jaw,
    jawY: down(BASE.jawY),
    chinY: down(BASE.chinY),
    chinW: BASE.chinW * face.chin,
    eyeY,
    eyeDx: BASE.eyeDx * face.eyeSpacing,
    eyeW: BASE.eyeW * face.eyeSize,
    eyeH: BASE.eyeH * face.eyeSize,
    lid: face.lid,
    slant: BASE.slant * face.eyeSlant,
    // A heavy brow ridge sits *lower* over the eye and weighs more. Raising it
    // instead is how a face ends up looking startled, which is what all five
    // heroes did until the brow was pulled back down onto the lid.
    browY: Math.max(4.5, BASE.browY - (face.browRidge - 1) * 3),
    browTilt: BASE.browTilt,
    browWeight: BASE.browWeight * (0.72 + 0.34 * face.browRidge),
    browW: BASE.browW * face.temple,
    noseY: down(BASE.noseY),
    noseW: BASE.noseW,
    mouthY: down(BASE.mouthY),
    mouthW: BASE.mouthW * face.mouthWidth,
    mouthCurve: look.bearing.mouth,
    neck: BASE.neck * face.neck,
    tilt: look.bearing.tilt,
    age: look.age,
  };
}

function skinOf(face: RaceFace): Skin {
  return { base: face.skin[0], shade: face.skin[1], deep: face.skin[2] };
}

/* ── Race parts: the body ────────────────────────────────────────────────── */

/**
 * Ears, and the one rule is that they must break the outline.
 *
 * An ear drawn inside the head's silhouette is a texture nobody sees at 46px;
 * drawn past it, it is the whole identification. That was proved on the troop
 * cards, where the elves' ears are the only thing separating them from men at
 * card size and they work precisely because they clear the skull.
 */
function raceEars(f: Shape, s: Skin, face: RaceFace): string {
  // Anchored on the eyeline, not on the cheekbone.
  //
  // It was `cheekY + 1`, which is fine on a human face and wrong on a gaunt one:
  // `cheekHeight` runs to 0.22 on the elves, which puts their cheekbone four
  // units *above* the eyeline and took the ears up with it, so a long swept ear
  // grew out of the corner of the eye and read across the face like a
  // moustache. An ear spans roughly brow to nose whatever the skull is doing
  // underneath, so it hangs off the eyeline and only its width follows the
  // cheek.
  const y = f.eyeY + 1.5;
  const w = f.cheek;
  const L = face.earLength;
  const near = hex(s.base);
  const far = hex(s.shade);
  switch (face.ear) {
    case 'none':
      return '';
    case 'long':
      // Straight out and back, well past the skull.
      return (
        `<path d="M${50 + w - 2} ${y - 4} L${50 + w + 7 * L} ${y - 9 * L} L${50 + w + 1} ${y + 5} Z" fill="${near}"/>` +
        `<path d="M${50 - w + 2} ${y - 4} L${50 - w - 7 * L} ${y - 9 * L} L${50 - w - 1} ${y + 5} Z" fill="${far}"/>`
      );
    case 'swept':
      // Held low and raked hard backward, with a notch bitten out of the
      // trailing edge.
      //
      // This was the same upward wedge as `long`, and it did not matter until
      // the selection rail put four elf kingdoms in a run of eight rows, dimmed,
      // at 44px — where eight identical pointed ovals was the worst block on the
      // sheet. `long` goes up and out; `swept` goes back and slightly *down*,
      // which is a different angle against a shared left edge and therefore a
      // different row. The notch does the rest at full size.
      return (
        `<path d="M${50 + w - 2} ${y - 2} C${50 + w + 4 * L} ${y - 1} ${50 + w + 8 * L} ${y + 1.5 * L} ${50 + w + 9.5 * L} ${y + 4 * L} ` +
        `L${50 + w + 5.5 * L} ${y + 2.6 * L} L${50 + w + 6.5 * L} ${y + 5.5 * L} ` +
        `C${50 + w + 3} ${y + 5} ${50 + w + 1} ${y + 6} ${50 + w - 1} ${y + 6} Z" fill="${near}"/>` +
        `<path d="M${50 - w + 2} ${y - 2} C${50 - w - 4 * L} ${y - 1} ${50 - w - 8 * L} ${y + 1.5 * L} ${50 - w - 9.5 * L} ${y + 4 * L} ` +
        `L${50 - w - 5.5 * L} ${y + 2.6 * L} L${50 - w - 6.5 * L} ${y + 5.5 * L} ` +
        `C${50 - w - 3} ${y + 5} ${50 - w - 1} ${y + 6} ${50 - w + 2} ${y + 6} Z" fill="${far}"/>`
      );
    case 'lobed':
      // A heavy rounded paddle, and the only ear in the roster with no point on
      // it anywhere.
      //
      // Added for one house — the Crimson Assize — because the selection rail is
      // kin-grouped and four elf kingdoms are four consecutive rows against a
      // shared left edge. Two of them shared `swept`, and inside one kin
      // round-against-pointed is the strongest outline contrast available, which
      // is the only kind that survives a dimmed row at 46px. It is drawn the
      // same pass it was added: a vocabulary value with no case behind it looks
      // supported, typechecks, passes tests and quietly draws something else.
      return (
        `<path d="M${50 + w - 2} ${y - 5} C${50 + w + 7 * L} ${y - 6 * L} ${50 + w + 8 * L} ${y + 4.5 * L} ${50 + w + 1.5} ${y + 6.5} ` +
        `C${50 + w - 1} ${y + 5} ${50 + w - 2} ${y} ${50 + w - 2} ${y - 5} Z" fill="${near}"/>` +
        // One fold, so a paddle is an ear and not a mitten.
        `<path d="M${50 + w + 0.5} ${y - 3} C${50 + w + 4.5 * L} ${y - 3} ${50 + w + 5 * L} ${y + 2.5 * L} ${50 + w + 1} ${y + 4}" ` +
        `stroke="${hex(s.deep)}" stroke-width="0.7" fill="none" opacity="0.45"/>` +
        `<path d="M${50 - w + 2} ${y - 5} C${50 - w - 7 * L} ${y - 6 * L} ${50 - w - 8 * L} ${y + 4.5 * L} ${50 - w - 1.5} ${y + 6.5} ` +
        `C${50 - w + 1} ${y + 5} ${50 - w + 2} ${y} ${50 - w + 2} ${y - 5} Z" fill="${far}"/>`
      );
    case 'batwing':
      // Wide, ribbed, and held out sideways rather than up. Sideways is the
      // whole point: anything this size pointing upward is a pair of horns.
      return (
        `<path d="M${50 + w - 2} ${y - 4} C${50 + w + 8 * L} ${y - 7 * L} ${50 + w + 11 * L} ${y - 1} ${50 + w + 9 * L} ${y + 6} ` +
        `C${50 + w + 4} ${y + 5} ${50 + w} ${y + 6} ${50 + w - 1} ${y + 6} Z" fill="${near}"/>` +
        `<path d="M${50 + w + 1} ${y - 1} L${50 + w + 7 * L} ${y + 1} M${50 + w + 1} ${y + 2} L${50 + w + 6 * L} ${y + 4}" ` +
        `stroke="${hex(s.deep)}" stroke-width="0.6" opacity="0.5"/>` +
        `<path d="M${50 - w + 2} ${y - 4} C${50 - w - 8 * L} ${y - 7 * L} ${50 - w - 11 * L} ${y - 1} ${50 - w - 9 * L} ${y + 6} ` +
        `C${50 - w - 4} ${y + 5} ${50 - w} ${y + 6} ${50 - w + 1} ${y + 6} Z" fill="${far}"/>`
      );
    case 'tufted': {
      // A beast's ear: a cone with a dark inner, and it is the one form that is
      // anchored on the *skull* rather than the eyeline. Moving every ear down
      // to the eyeline fixed the elves and broke this one — a wolf's ear sits on
      // top of the head, and at eye level it stopped breaking the outline and
      // became two dark wedges lying across the face.
      const ty = Math.min(y - 6, f.crown + 5);
      return (
        `<path d="M${50 + w - 5} ${ty} L${50 + w - 1} ${ty - 13 * L} L${50 + w + 5} ${ty + 2} Z" fill="${near}"/>` +
        `<path d="M${50 + w - 3.4} ${ty - 0.6} L${50 + w - 1} ${ty - 10 * L} L${50 + w + 3.2} ${ty + 1} Z" fill="${hex(0x2a241d)}"/>` +
        `<path d="M${50 - w + 5} ${ty} L${50 - w + 1} ${ty - 13 * L} L${50 - w - 5} ${ty + 2} Z" fill="${far}"/>` +
        `<path d="M${50 - w + 3.4} ${ty - 0.6} L${50 - w + 1} ${ty - 10 * L} L${50 - w - 3.2} ${ty + 1} Z" fill="${hex(0x221d18)}"/>`
      );
    }
    case 'fin':
      // A fan of spines, held low and back.
      return (
        `<path d="M${50 + w - 2} ${y - 2} L${50 + w + 8 * L} ${y - 7} L${50 + w + 7 * L} ${y - 1} L${50 + w + 9 * L} ${y + 3} ` +
        `L${50 + w + 5} ${y + 4} L${50 + w - 1} ${y + 6} Z" fill="${near}"/>` +
        `<path d="M${50 - w + 2} ${y - 2} L${50 - w - 8 * L} ${y - 7} L${50 - w - 7 * L} ${y - 1} L${50 - w - 9 * L} ${y + 3} ` +
        `L${50 - w - 5} ${y + 4} L${50 - w + 1} ${y + 6} Z" fill="${far}"/>`
      );
    case 'cropped':
      // Cut off. A deliberate absence needs a scarred stub, or it reads as an
      // ear the artist forgot rather than one somebody removed.
      return (
        `<path d="M${50 + w - 1} ${y - 2} L${50 + w + 2.4} ${y - 3} L${50 + w + 2} ${y + 3} L${50 + w - 1} ${y + 3.5} Z" fill="${near}"/>` +
        `<path d="M${50 + w + 0.5} ${y - 2.6} L${50 + w + 2.2} ${y + 3}" stroke="${hex(s.deep)}" stroke-width="0.7" opacity="0.7"/>` +
        `<path d="M${50 - w + 1} ${y - 2} L${50 - w - 2.4} ${y - 3} L${50 - w - 2} ${y + 3} L${50 - w + 1} ${y + 3.5} Z" fill="${far}"/>`
      );
    case 'round':
      return (
        `<circle cx="${50 + w + 1}" cy="${y - 3}" r="${4.2 * L}" fill="${near}"/>` +
        `<circle cx="${50 + w + 1}" cy="${y - 3}" r="${2.3 * L}" fill="${hex(s.deep)}"/>` +
        `<circle cx="${50 - w - 1}" cy="${y - 3}" r="${4.2 * L}" fill="${far}"/>`
      );
    default:
      return ears(f, s);
  }
}

/**
 * A muzzle, drawn as mass — the one place this file is allowed to.
 *
 * The standing rule is that a nose can only be implied, because a nose drawn
 * with mass reads as a snout; all five heroes grew one the day it was tried. For
 * a people meant to *have* a snout that stops being a fault and becomes the
 * method, so the muzzle is a real lobe with a nose pad on it and `features` is
 * told to skip the human nose underneath.
 */
function muzzleArt(f: Shape, s: Skin, m: number): string {
  const w = f.mouthW * (0.9 + m * 0.9);
  const top = f.noseY - 6 * m;
  const tip = f.mouthY + 3.5 * m;
  return (
    `<path d="M${50 - w} ${top + 3} C${50 - w - 1} ${tip - 2} ${50 - w * 0.5} ${tip} 50 ${tip} ` +
    `C${50 + w * 0.5} ${tip} ${50 + w + 1} ${tip - 2} ${50 + w} ${top + 3} ` +
    `C${50 + w * 0.7} ${top - 1} ${50 - w * 0.7} ${top - 1} ${50 - w} ${top + 3} Z" fill="${hex(s.base)}"/>` +
    `<path d="M50 ${top - 0.6} C${50 - w * 0.7} ${top - 1} ${50 - w} ${top + 3} ${50 - w} ${top + 3} ` +
    `C${50 - w - 1} ${tip - 2} ${50 - w * 0.5} ${tip} 50 ${tip} ` +
    `C${50 - w * 0.2} ${tip - 3} ${50 - w * 0.25} ${top + 2} 50 ${top - 0.6} Z" fill="${hex(s.shade)}" opacity="0.5"/>` +
    // The pad. It is the single mark that says animal rather than deformity.
    `<path d="M${50 - w * 0.62} ${top + 1.4} C${50 - w * 0.62} ${top - 1.6} ${50 + w * 0.62} ${top - 1.6} ${50 + w * 0.62} ${top + 1.4} ` +
    `C${50 + w * 0.3} ${top + 3.6} ${50 - w * 0.3} ${top + 3.6} ${50 - w * 0.62} ${top + 1.4} Z" fill="${hex(0x2b2420)}"/>` +
    `<path d="M${50 - w * 0.34} ${top + 0.2} C${50 - w * 0.1} ${top - 0.8} ${50 + w * 0.1} ${top - 0.8} ${50 + w * 0.2} ${top - 0.2}" ` +
    `stroke="#fff" stroke-width="0.8" fill="none" opacity="0.2" stroke-linecap="round"/>` +
    `<path d="M50 ${top + 3.6} L50 ${tip - 3.6}" stroke="${hex(s.deep)}" stroke-width="0.8" opacity="0.5"/>`
  );
}

/** Tusks. Two white triangles remain the cheapest "not a man" available. */
function tusks(f: Shape, face: RaceFace): string {
  const t = face.tuskSize * 7;
  if (face.tusk === 'none' || t <= 0) return '';
  const x = f.mouthW * 0.82;
  const y = f.mouthY;
  if (face.tusk === 'upper') {
    return (
      `<path d="M${50 - x} ${y - 0.5} L${50 - x - 1.5} ${y + t} L${50 - x + 1.4} ${y + 0.4} Z" fill="${hex(0xe6ddc6)}"/>` +
      `<path d="M${50 + x} ${y - 0.5} L${50 + x + 1.5} ${y + t} L${50 + x - 1.4} ${y + 0.4} Z" fill="${hex(0xf2ebd8)}"/>`
    );
  }
  // Lower and boar both come up past the lip; boar sweeps outward as well.
  const out = face.tusk === 'boar' ? 3.4 : 1.2;
  return (
    `<path d="M${50 - x} ${y + 1.5} L${50 - x - out} ${y - t} L${50 - x + 1.6} ${y + 1} Z" fill="${hex(0xded4bb)}"/>` +
    `<path d="M${50 + x} ${y + 1.5} L${50 + x + out} ${y - t} L${50 + x - 1.6} ${y + 1} Z" fill="${hex(0xf2ebd8)}"/>`
  );
}

/**
 * A crest, under the rule from the contract: commit to it or leave it off.
 *
 * Anything sharp above the hairline reads as horns whether it was meant to or
 * not — that fault cost the Huntmaster his hood, and then his fringe. So a crest
 * that is wanted is drawn large enough to be unmistakably itself, and `none`
 * means nothing pointed up there at all.
 */
function crestArt(f: Shape, kit: Kit, face: RaceFace): string {
  const c = face.crestSize * 10;
  if (face.crest === 'none' || c <= 0) return '';
  const y = f.crown + 2;
  const w = f.skull;
  const pale = hex(0xd9cfb6);
  const dark = hex(0x9a8f78);
  switch (face.crest) {
    case 'horn-ram':
      // Curled down and forward around the ear, not up. A ram's horn is a
      // spiral and reads as one at any size; two spikes read as a devil.
      return (
        `<path d="M${50 - w + 1} ${y - 2} C${50 - w - c * 0.9} ${y - c * 0.5} ${50 - w - c * 1.15} ${y + c * 0.7} ${50 - w - c * 0.5} ${y + c * 1.15} ` +
        `C${50 - w - c * 0.05} ${y + c * 1.3} ${50 - w + c * 0.15} ${y + c * 0.8} ${50 - w - c * 0.05} ${y + c * 0.55} ` +
        `C${50 - w - c * 0.5} ${y + c * 0.75} ${50 - w - c * 0.6} ${y + c * 0.1} ${50 - w + 2} ${y + 2} Z" fill="${dark}"/>` +
        `<path d="M${50 + w - 1} ${y - 2} C${50 + w + c * 0.9} ${y - c * 0.5} ${50 + w + c * 1.15} ${y + c * 0.7} ${50 + w + c * 0.5} ${y + c * 1.15} ` +
        `C${50 + w + c * 0.05} ${y + c * 1.3} ${50 + w - c * 0.15} ${y + c * 0.8} ${50 + w + c * 0.05} ${y + c * 0.55} ` +
        `C${50 + w + c * 0.5} ${y + c * 0.75} ${50 + w + c * 0.6} ${y + c * 0.1} ${50 + w - 2} ${y + 2} Z" fill="${pale}"/>` +
        `<path d="M${50 + w + c * 0.2} ${y + c * 0.1} L${50 + w + c * 0.55} ${y + c * 0.35} M${50 + w + c * 0.35} ${y + c * 0.5} L${50 + w + c * 0.62} ${y + c * 0.72}" ` +
        `stroke="${dark}" stroke-width="0.7" opacity="0.6"/>`
      );
    case 'antler':
      return (
        `<path d="M${50 - w * 0.6} ${y} L${50 - w - c * 0.4} ${y - c * 1.4} M${50 - w - c * 0.2} ${y - c * 0.7} L${50 - w - c * 0.9} ${y - c * 0.9} ` +
        `M${50 + w * 0.6} ${y} L${50 + w + c * 0.4} ${y - c * 1.4} M${50 + w + c * 0.2} ${y - c * 0.7} L${50 + w + c * 0.9} ${y - c * 0.9}" ` +
        `stroke="${pale}" stroke-width="2.2" fill="none" stroke-linecap="round"/>`
      );
    case 'frill':
      return (
        `<path d="M${50 - w} ${y + 4} C${50 - w - c * 0.4} ${y - c} ${50 + w + c * 0.4} ${y - c} ${50 + w} ${y + 4} Z" ` +
        `fill="${hex(kit.accent)}" opacity="0.5"/>` +
        `<path d="M${50 - w * 0.5} ${y - c * 0.5} v${c * 0.5} M50 ${y - c * 0.68} v${c * 0.6} M${50 + w * 0.5} ${y - c * 0.5} v${c * 0.5}" ` +
        `stroke="${dark}" stroke-width="0.9" opacity="0.7"/>`
      );
    case 'quill':
      return (
        `<path d="M${50 - w * 0.8} ${y + 2} L${50 - w * 1.1} ${y - c} M${50 - w * 0.3} ${y} L${50 - w * 0.35} ${y - c * 1.2} ` +
        `M${50 + w * 0.3} ${y} L${50 + w * 0.35} ${y - c * 1.2} M${50 + w * 0.8} ${y + 2} L${50 + w * 1.1} ${y - c}" ` +
        `stroke="${dark}" stroke-width="1.4" stroke-linecap="round"/>`
      );
    default:
      // horn-pair, horn-swept, bone-plate: a heavy pair swept back off the brow.
      return (
        `<path d="M${50 - w + 1} ${y} C${50 - w - c * 0.5} ${y - c * 0.4} ${50 - w - c} ${y - c * 1.1} ${50 - w - c * 0.8} ${y - c * 1.6} ` +
        `C${50 - w - c * 0.3} ${y - c * 0.9} ${50 - w + 1} ${y - c * 0.3} ${50 - w + 3} ${y + 1} Z" fill="${dark}"/>` +
        `<path d="M${50 + w - 1} ${y} C${50 + w + c * 0.5} ${y - c * 0.4} ${50 + w + c} ${y - c * 1.1} ${50 + w + c * 0.8} ${y - c * 1.6} ` +
        `C${50 + w + c * 0.3} ${y - c * 0.9} ${50 + w - 1} ${y - c * 0.3} ${50 + w - 3} ${y + 1} Z" fill="${pale}"/>`
      );
  }
}

/** Hide: a handful of marks over the skin ramp. Never a repeat — at 46px a pattern is noise. */
function hideMarks(f: Shape, s: Skin, face: RaceFace): string {
  const d = hex(s.deep);
  switch (face.hide) {
    case 'scaled':
      return (
        `<path d="M${50 + f.cheek * 0.45} ${f.cheekY + 2} a2 2 0 0 1 4 0 M${50 + f.cheek * 0.35} ${f.cheekY + 6} a2 2 0 0 1 4 0 ` +
        `M${50 + f.cheek * 0.55} ${f.cheekY + 10} a2 2 0 0 1 4 0" stroke="${d}" stroke-width="0.6" fill="none" opacity="0.45"/>`
      );
    case 'warty':
      return (
        `<circle cx="${50 + f.cheek * 0.5}" cy="${f.cheekY + 4}" r="1.1" fill="${d}" opacity="0.4"/>` +
        `<circle cx="${50 + f.cheek * 0.32}" cy="${f.cheekY + 9}" r="0.8" fill="${d}" opacity="0.35"/>` +
        `<circle cx="${50 - f.cheek * 0.55}" cy="${f.cheekY + 6}" r="0.9" fill="${d}" opacity="0.3"/>` +
        `<circle cx="${50 + f.cheek * 0.2}" cy="${f.crown + 5}" r="1" fill="${d}" opacity="0.3"/>`
      );
    case 'furred':
      return (
        `<path d="M${50 + f.cheek * 0.7} ${f.cheekY + 2} l3 -1.5 M${50 + f.cheek * 0.72} ${f.cheekY + 6} l3.2 -0.6 ` +
        `M${50 - f.cheek * 0.7} ${f.cheekY + 3} l-3 -1.5 M${50 - f.cheek * 0.72} ${f.cheekY + 7} l-3.2 -0.6" ` +
        `stroke="${hex(s.shade)}" stroke-width="0.8" opacity="0.6" stroke-linecap="round"/>`
      );
    case 'wrapped':
      // A binding across the lower face. It also answers what to do about a
      // mouth on a people who are not meant to show one.
      return (
        `<path d="M${50 - f.cheek - 1} ${f.eyeY + 5} L${50 + f.cheek + 1} ${f.eyeY + 1} L${50 + f.cheek + 1} ${f.eyeY + 5.5} ` +
        `L${50 - f.cheek - 1} ${f.eyeY + 9.5} Z" fill="${hex(0x8d8574)}" opacity="0.85"/>` +
        `<path d="M${50 - f.cheek - 1} ${f.eyeY + 7.4} L${50 + f.cheek + 1} ${f.eyeY + 3.2}" stroke="#000" stroke-width="0.5" opacity="0.3"/>`
      );
    case 'chitin':
      return (
        `<path d="M${50 - f.cheek} ${f.cheekY - 2} C${50 - f.cheek * 0.4} ${f.cheekY - 5} ${50 + f.cheek * 0.4} ${f.cheekY - 5} ${50 + f.cheek} ${f.cheekY - 2}" ` +
        `stroke="${d}" stroke-width="0.9" fill="none" opacity="0.5"/>`
      );
    case 'bare':
      // Bone. The sutures are what stop a skull reading as an egg.
      return (
        `<path d="M50 ${f.crown + 1} L50 ${f.eyeY - f.browY - 3}" stroke="${d}" stroke-width="0.6" opacity="0.4"/>` +
        `<path d="M${50 - f.skull * 0.7} ${f.crown + 5} C${50 - f.skull * 0.3} ${f.crown + 3} ${50 + f.skull * 0.3} ${f.crown + 3} ${50 + f.skull * 0.7} ${f.crown + 5}" ` +
        `stroke="${d}" stroke-width="0.6" fill="none" opacity="0.35"/>` +
        `<path d="M${50 - f.cheek * 0.75} ${f.cheekY + 5} L${50 - f.cheek * 0.45} ${f.jawY - 2} M${50 + f.cheek * 0.75} ${f.cheekY + 5} L${50 + f.cheek * 0.45} ${f.jawY - 2}" ` +
        `stroke="${d}" stroke-width="0.7" opacity="0.4"/>`
      );
    default:
      return '';
  }
}

/* ── House parts: the culture ────────────────────────────────────────────── */

/**
 * What is worn on the head, and it is the strongest identifier after the face.
 *
 * `behind` goes under the skull, `front` over it — the split matters, and the
 * Prelate's mitre proved why: drawn behind the head, everything below the
 * hairline was covered and all that cleared it was a peak on a gold bar. A
 * `closed` helm means no face is drawn under it at all, which is both cheaper
 * and far more menacing than a face behind a grille nobody can resolve.
 *
 * `rise` is how far above the crown the piece reaches, and the frame uses it to
 * work out its own headroom rather than making twenty-two people hand-tune a
 * crop each.
 */
function crownArt(
  f: Shape,
  kit: Kit,
  look: HouseLook,
): { behind: string; front: string; closed: boolean; rise: number } {
  const brow = f.eyeY - f.browY - 2;
  const w = f.skull;
  const metal = hex(kit.metal);
  const lit = hex(kit.lit);
  const deep = hex(kit.deep);
  const none = { behind: '', front: '', closed: false, rise: 0 };

  /** A dome over the skull, shared by all three helms. */
  const dome = (extra: number): string =>
    `<path d="M${50 - w - 2} ${brow + 1} C${50 - w - 2.4} ${f.crown - 6 - extra} ${50 + w + 2.4} ${f.crown - 6 - extra} ${50 + w + 2} ${brow + 1} Z" fill="${metal}"/>` +
    `<path d="M50 ${f.crown - 5 - extra} C${50 + w * 0.7} ${f.crown - 4.6 - extra} ${50 + w + 2} ${brow - 4} ${50 + w + 2} ${brow + 1} ` +
    `C${50 + w * 0.6} ${brow - 2.4} ${50 + 2} ${f.crown - 3 - extra} 50 ${f.crown - 5 - extra} Z" fill="${lit}" opacity="0.75"/>`;

  switch (look.crown) {
    case 'bare':
      return none;

    case 'circlet':
      return {
        behind: '',
        front:
          `<path d="M${50 - w - 1.5} ${brow - 2} C${50 - w * 0.5} ${brow - 5} ${50 + w * 0.5} ${brow - 5} ${50 + w + 1.5} ${brow - 2} ` +
          `L${50 + w + 1.5} ${brow} C${50 + w * 0.5} ${brow - 3} ${50 - w * 0.5} ${brow - 3} ${50 - w - 1.5} ${brow} Z" fill="${metal}"/>` +
          `<path d="M50 ${brow - 7} L${50 + 2.6} ${brow - 3.5} L50 ${brow - 0.6} L${50 - 2.6} ${brow - 3.5} Z" fill="${lit}"/>`,
        closed: false,
        rise: 4,
      };

    case 'crown-heavy':
      // A tall band with five points. The points are rounded at the tip on
      // purpose — sharpened, five of them above a brow are a crown of thorns.
      return {
        behind: '',
        front:
          `<path d="M${50 - w - 2} ${brow - 1} L${50 - w - 2} ${brow - 7} L${50 - w * 0.55} ${brow - 3} L${50 - w * 0.2} ${brow - 11} ` +
          `L${50 + w * 0.2} ${brow - 11} L${50 + w * 0.55} ${brow - 3} L${50 + w + 2} ${brow - 7} L${50 + w + 2} ${brow - 1} Z" fill="${metal}"/>` +
          `<path d="M50 ${brow - 11} L${50 + w * 0.2} ${brow - 11} L${50 + w * 0.55} ${brow - 3} L${50 + w + 2} ${brow - 7} L${50 + w + 2} ${brow - 1} L50 ${brow - 1} Z" ` +
          `fill="${lit}" opacity="0.55"/>` +
          `<path d="M${50 - w - 2} ${brow - 1} L${50 + w + 2} ${brow - 1} L${50 + w + 2} ${brow + 2.4} L${50 - w - 2} ${brow + 2.4} Z" fill="${deep}"/>` +
          `<circle cx="50" cy="${brow + 0.6}" r="1.6" fill="${hex(kit.accent)}"/>`,
        closed: false,
        rise: 13,
      };

    case 'mitre':
      return { behind: '', front: mitre({ cloth: kit.cloth, clothLight: kit.clothLight, metal: kit.metal }, f), closed: false, rise: 21 };

    case 'coif':
      // One closed arc shoulder to shoulder, with the face opening cut out of
      // it. Mail is drawn as a value break, never as rings — rings at this size
      // are noise.
      return {
        behind:
          `<path d="M${50 - w - 4.5} ${f.chinY + 8} C${50 - w - 5.5} ${f.crown - 8} ${50 - w * 0.6} ${f.crown - 11} 50 ${f.crown - 11} ` +
          `C${50 + w * 0.6} ${f.crown - 11} ${50 + w + 5.5} ${f.crown - 8} ${50 + w + 4.5} ${f.chinY + 8} ` +
          `L${50 + w + 7} ${f.chinY + 16} L${50 - w - 7} ${f.chinY + 16} Z" fill="${metal}"/>` +
          `<path d="M${50 - w - 4.5} ${f.chinY + 8} C${50 - w - 5.5} ${f.crown - 8} ${50 - w * 0.6} ${f.crown - 11} 50 ${f.crown - 11} ` +
          `C${50 + w * 0.2} ${f.crown - 11} ${50 + w * 0.5} ${f.crown - 9} ${50 + w * 0.5} ${f.crown - 6} ` +
          `C${50 - w * 0.2} ${f.crown - 4} ${50 - w - 1} ${f.cheekY} ${50 - w - 1.5} ${f.chinY + 8} Z" fill="#000" opacity="0.3"/>` +
          `<path d="M${50 - w - 0.5} ${f.cheekY - 2} C${50 - w - 0.5} ${f.jawY + 4} ${50 - f.chinW * 2} ${f.chinY + 3} 50 ${f.chinY + 3} ` +
          `C${50 + f.chinW * 2} ${f.chinY + 3} ${50 + w + 0.5} ${f.jawY + 4} ${50 + w + 0.5} ${f.cheekY - 2} ` +
          `C${50 + w + 0.5} ${f.crown - 3} ${50 + w * 0.6} ${f.crown - 6} 50 ${f.crown - 6} ` +
          `C${50 - w * 0.6} ${f.crown - 6} ${50 - w - 0.5} ${f.crown - 3} ${50 - w - 0.5} ${f.cheekY - 2} Z" fill="${deep}"/>`,
        front: '',
        closed: false,
        rise: 11,
      };

    case 'veil':
      // Cloth from the crown, hanging past the jaw. Behind the head, so the
      // face stays clear — a veil that covers the face is a mask, and there is
      // a separate value for that.
      return {
        behind:
          `<path d="M${50 - w - 6} ${f.chinY + 18} C${50 - w - 7} ${f.crown - 4} ${50 - w * 0.5} ${f.crown - 9} 50 ${f.crown - 9} ` +
          `C${50 + w * 0.5} ${f.crown - 9} ${50 + w + 7} ${f.crown - 4} ${50 + w + 6} ${f.chinY + 18} Z" fill="${hex(kit.cloth)}"/>` +
          `<path d="M50 ${f.crown - 9} C${50 + w * 0.5} ${f.crown - 9} ${50 + w + 7} ${f.crown - 4} ${50 + w + 6} ${f.chinY + 18} ` +
          `L${50 + w - 1} ${f.chinY + 18} C${50 + w} ${f.cheekY} ${50 + w * 0.4} ${f.crown - 5} 50 ${f.crown - 5} Z" ` +
          `fill="${hex(kit.clothLight)}" opacity="0.6"/>`,
        front:
          `<path d="M${50 - w - 2.5} ${brow - 1} C${50 - w * 0.5} ${brow - 4.4} ${50 + w * 0.5} ${brow - 4.4} ${50 + w + 2.5} ${brow - 1} ` +
          `L${50 + w + 2.5} ${brow + 1.8} C${50 + w * 0.5} ${brow - 1.6} ${50 - w * 0.5} ${brow - 1.6} ${50 - w - 2.5} ${brow + 1.8} Z" fill="${metal}"/>`,
        closed: false,
        rise: 9,
      };

    case 'mask':
      // A plate over the upper face with two cut slots. Drawn in front of the
      // eyes, which is the point of it.
      return {
        behind: '',
        front:
          `<path d="M${50 - f.cheek - 1} ${f.eyeY + 6} C${50 - f.cheek - 2} ${f.crown + 2} ${50 - w * 0.5} ${f.crown - 3} 50 ${f.crown - 3} ` +
          `C${50 + w * 0.5} ${f.crown - 3} ${50 + f.cheek + 2} ${f.crown + 2} ${50 + f.cheek + 1} ${f.eyeY + 6} ` +
          `C${50 + f.cheek * 0.4} ${f.eyeY + 9} ${50 - f.cheek * 0.4} ${f.eyeY + 9} ${50 - f.cheek - 1} ${f.eyeY + 6} Z" fill="${metal}"/>` +
          `<path d="M50 ${f.crown - 3} C${50 + w * 0.5} ${f.crown - 3} ${50 + f.cheek + 2} ${f.crown + 2} ${50 + f.cheek + 1} ${f.eyeY + 6} ` +
          `C${50 + f.cheek * 0.5} ${f.eyeY + 8.4} ${50 + 2} ${f.eyeY + 4} 50 ${f.crown - 3} Z" fill="${lit}" opacity="0.5"/>` +
          `<path d="M${50 - f.eyeDx - f.eyeW - 1} ${f.eyeY - 1.4} L${50 - f.eyeDx + f.eyeW + 1} ${f.eyeY - 0.4} L${50 - f.eyeDx + f.eyeW + 0.6} ${f.eyeY + 1.6} L${50 - f.eyeDx - f.eyeW - 1} ${f.eyeY + 0.8} Z" fill="${hex(0x0d0b09)}"/>` +
          `<path d="M${50 + f.eyeDx + f.eyeW + 1} ${f.eyeY - 1.4} L${50 + f.eyeDx - f.eyeW - 1} ${f.eyeY - 0.4} L${50 + f.eyeDx - f.eyeW - 0.6} ${f.eyeY + 1.6} L${50 + f.eyeDx + f.eyeW + 1} ${f.eyeY + 0.8} Z" fill="${hex(0x0d0b09)}"/>` +
          `<path d="M50 ${f.eyeY + 2} L50 ${f.eyeY + 8}" stroke="${deep}" stroke-width="0.8"/>` +
          // The shadow it casts on the face beneath. Without it the plate and
          // the jaw below read as two stacked heads rather than as a mask worn
          // over one, and a pale material makes that worse rather than better.
          `<path d="M${50 - f.cheek - 1} ${f.eyeY + 6} C${50 - f.cheek * 0.4} ${f.eyeY + 9} ${50 + f.cheek * 0.4} ${f.eyeY + 9} ${50 + f.cheek + 1} ${f.eyeY + 6} ` +
          `L${50 + f.cheek + 1} ${f.eyeY + 8.6} C${50 + f.cheek * 0.4} ${f.eyeY + 11.6} ${50 - f.cheek * 0.4} ${f.eyeY + 11.6} ${50 - f.cheek - 1} ${f.eyeY + 8.6} Z" ` +
          `fill="#000" opacity="0.3"/>`,
        closed: false,
        rise: 6,
      };

    case 'helm-open':
      return {
        behind: '',
        front:
          dome(2) +
          // The nasal bar. It is the read, and without it this is a bowl.
          `<path d="M${50 - 1.4} ${brow} L${50 + 1.4} ${brow} L${50 + 1.1} ${f.noseY - 1} L${50 - 1.1} ${f.noseY - 1} Z" fill="${metal}"/>` +
          `<path d="M${50 - w - 3} ${brow} L${50 + w + 3} ${brow} L${50 + w + 2.4} ${brow + 2.6} L${50 - w - 2.4} ${brow + 2.6} Z" fill="${deep}"/>`,
        closed: false,
        rise: 9,
      };

    case 'helm-closed':
    case 'helm-horned': {
      const horned = look.crown === 'helm-horned';
      const c = 9;
      return {
        behind: '',
        front:
          dome(3) +
          `<path d="M${50 - w - 2} ${brow + 1} L${50 - w - 1} ${f.jawY + 2} C${50 - f.chinW * 2} ${f.chinY + 3} ${50 + f.chinW * 2} ${f.chinY + 3} ${50 + w + 1} ${f.jawY + 2} ` +
          `L${50 + w + 2} ${brow + 1} Z" fill="${metal}"/>` +
          `<path d="M${50 + w + 2} ${brow + 1} L${50 + w + 1} ${f.jawY + 2} C${50 + f.chinW * 1.2} ${f.chinY + 2.6} ${50 + 2} ${f.chinY + 2.4} ${50 + 1.6} ${f.chinY + 2.4} ` +
          `C${50 + 3.4} ${f.jawY} ${50 + 3} ${f.eyeY} ${50 + 2.6} ${brow + 1} Z" fill="${lit}" opacity="0.4"/>` +
          // A cross slit rather than a grille: at 46px a grille is a grey patch.
          `<path d="M${50 - w + 0.4} ${f.eyeY - 1} L${50 + w - 0.4} ${f.eyeY - 1} L${50 + w - 0.4} ${f.eyeY + 1.4} L${50 - w + 0.4} ${f.eyeY + 1.4} Z" fill="${hex(0x0b0908)}"/>` +
          `<path d="M${50 - 1} ${f.eyeY - 1} L${50 + 1} ${f.eyeY - 1} L${50 + 1} ${f.noseY + 3} L${50 - 1} ${f.noseY + 3} Z" fill="${hex(0x0b0908)}"/>` +
          // The brow band, the jaw seam and four rivets. Without them a closed
          // helm is a flat plate with a slot in it, which is what it was — and
          // at frameScale 1.18 that plate is half the picture. Armour needs at
          // least one horizontal to sit against, the same way the Warlord's
          // gorget says soldier before it says anything else.
          `<path d="M${50 - w - 2} ${f.eyeY - 4.6} L${50 + w + 2} ${f.eyeY - 4.6} L${50 + w + 1.6} ${f.eyeY - 2.2} L${50 - w - 1.6} ${f.eyeY - 2.2} Z" fill="${deep}"/>` +
          `<path d="M${50 - w - 1.6} ${f.eyeY - 4.6} L${50 + w + 1.6} ${f.eyeY - 4.6} L${50 + w + 1.4} ${f.eyeY - 3.6} L${50 - w - 1.4} ${f.eyeY - 3.6} Z" fill="${lit}" opacity="0.5"/>` +
          `<path d="M${50 - w + 1} ${f.mouthY + 1} C${50 - f.chinW} ${f.mouthY + 3} ${50 + f.chinW} ${f.mouthY + 3} ${50 + w - 1} ${f.mouthY + 1}" ` +
          `stroke="${deep}" stroke-width="1" fill="none" opacity="0.8"/>` +
          `<circle cx="${50 - w + 1.6}" cy="${f.eyeY - 6.4}" r="0.85" fill="${deep}"/>` +
          `<circle cx="${50 + w - 1.6}" cy="${f.eyeY - 6.4}" r="0.85" fill="${lit}"/>` +
          `<circle cx="${50 - w + 2.2}" cy="${f.jawY}" r="0.85" fill="${deep}"/>` +
          `<circle cx="${50 + w - 2.2}" cy="${f.jawY}" r="0.85" fill="${lit}"/>` +
          (horned
            ? `<path d="M${50 - w - 1} ${brow - 1} C${50 - w - c} ${brow - c * 0.5} ${50 - w - c * 1.1} ${f.crown - c} ${50 - w - c * 0.5} ${f.crown - c * 1.5} ` +
              `C${50 - w - c * 0.7} ${f.crown - c * 0.6} ${50 - w - 2} ${brow - c * 0.4} ${50 - w + 2} ${brow + 1} Z" fill="${hex(0xcfc6b0)}"/>` +
              `<path d="M${50 + w + 1} ${brow - 1} C${50 + w + c} ${brow - c * 0.5} ${50 + w + c * 1.1} ${f.crown - c} ${50 + w + c * 0.5} ${f.crown - c * 1.5} ` +
              `C${50 + w + c * 0.7} ${f.crown - c * 0.6} ${50 + w + 2} ${brow - c * 0.4} ${50 + w - 2} ${brow + 1} Z" fill="${hex(0xe4dcc4)}"/>`
            : ''),
        closed: true,
        rise: horned ? 22 : 10,
      };
    }

    case 'wreath':
      return {
        behind: '',
        front:
          `<path d="M${50 - w - 1.5} ${brow - 1} C${50 - w * 0.5} ${brow - 4} ${50 + w * 0.5} ${brow - 4} ${50 + w + 1.5} ${brow - 1}" ` +
          `stroke="${metal}" stroke-width="1.4" fill="none"/>` +
          `<path d="M${50 - w * 0.85} ${brow - 2.6} l-2.4 -2.8 l3 0.4 Z M${50 - w * 0.3} ${brow - 3.6} l-2 -3 l2.9 0.6 Z ` +
          `M${50 + w * 0.3} ${brow - 3.6} l2 -3 l-2.9 0.6 Z M${50 + w * 0.85} ${brow - 2.6} l2.4 -2.8 l-3 0.4 Z" fill="${lit}"/>`,
        closed: false,
        rise: 7,
      };

    default: {
      // antler-rack: worn, not grown, so it is heavier and more symmetrical
      // than the `antler` crest and it sits on a browband.
      const c = 13;
      return {
        behind:
          `<path d="M${50 - w * 0.7} ${brow - 2} L${50 - w - c * 0.5} ${brow - c * 1.5} M${50 - w - c * 0.18} ${brow - c * 0.7} L${50 - w - c} ${brow - c * 0.8} ` +
          `M${50 - w - c * 0.34} ${brow - c * 1.1} L${50 - w - c * 0.95} ${brow - c * 1.35} ` +
          `M${50 + w * 0.7} ${brow - 2} L${50 + w + c * 0.5} ${brow - c * 1.5} M${50 + w + c * 0.18} ${brow - c * 0.7} L${50 + w + c} ${brow - c * 0.8} ` +
          `M${50 + w + c * 0.34} ${brow - c * 1.1} L${50 + w + c * 0.95} ${brow - c * 1.35}" ` +
          `stroke="${hex(0xcfc6b0)}" stroke-width="2" fill="none" stroke-linecap="round"/>`,
        front:
          `<path d="M${50 - w - 2} ${brow - 1} C${50 - w * 0.5} ${brow - 4.4} ${50 + w * 0.5} ${brow - 4.4} ${50 + w + 2} ${brow - 1} ` +
          `L${50 + w + 2} ${brow + 2} C${50 + w * 0.5} ${brow - 1.4} ${50 - w * 0.5} ${brow - 1.4} ${50 - w - 2} ${brow + 2} Z" fill="${metal}"/>`,
        closed: false,
        rise: c * 1.5 + 4,
      };
    }
  }
}

/**
 * Hair, as a cut rather than as a texture.
 *
 * `back` goes behind the head and `front` over the brow; a bust with only one
 * of the two reads flat, which is a lesson the heroes paid for. Nothing here
 * puts a point above the hairline — a topknot is a rounded knot set back off
 * the crown, and `wild` breaks sideways, because upward points are horns.
 */
function groomingArt(f: Shape, kit: Kit, face: RaceFace, look: HouseLook): { back: string; front: string } {
  const w = f.skull;
  const col = hex(kit.hair);
  const litc = hex(kit.hairLight);
  const brow = f.eyeY - f.browY - 2;
  const nothing = { back: '', front: '' };
  if (face.hairGrowth === 'none') return nothing;

  if (face.hairGrowth === 'crest') {
    // A mane down the centre line and bare at the temples.
    //
    // `hairGrowth` is a *race* field and it was being ignored, which is why the
    // two Moonbound kingdoms came out as one face twice on the first sheet: a
    // full cap of hair on a muzzled head reads as a wig, and both of them were
    // wearing the same one. A crest reads as an animal, and it leaves the cut
    // somewhere to differ — ragged and loose against bound and combed is a
    // difference you can see at 46px when two cropped caps are not.
    const mane = look.grooming === 'wild' || look.grooming === 'topknot' || look.grooming === 'long';
    const cw = w * (mane ? 0.42 : 0.26);
    return {
      back:
        `<path d="M${50 - cw} ${f.crown + 8} C${50 - cw - 1} ${f.crown - 7} ${50 + cw + 1} ${f.crown - 7} ${50 + cw} ${f.crown + 8} ` +
        `L${50 + cw * 0.8} ${f.chinY + 6} L${50 - cw * 0.8} ${f.chinY + 6} Z" fill="${col}"/>` +
        `<path d="M${50 + 0.5} ${f.crown - 6} C${50 + cw} ${f.crown - 5} ${50 + cw} ${f.crown + 4} ${50 + cw} ${f.crown + 8} ` +
        `L${50 + cw * 0.8} ${f.chinY + 6} L${50 + 0.5} ${f.chinY + 6} Z" fill="${litc}" opacity="0.4"/>` +
        (mane
          ? // Ragged, and breaking sideways off the mane rather than upward.
            `<path d="M${50 - cw} ${f.crown - 1} l-5 -2 l4 4 Z M${50 - cw} ${f.crown + 7} l-6 0 l5 3 Z ` +
            `M${50 + cw} ${f.crown - 3} l5 -1 l-4 4 Z M${50 + cw} ${f.crown + 6} l6 1 l-5 3 Z" fill="${col}"/>`
          : // Bound: three rings down the mane, which reads as combed and kept.
            `<path d="M${50 - cw - 0.5} ${f.crown + 6} h${cw * 2 + 1} M${50 - cw - 0.5} ${f.crown + 14} h${cw * 2 + 1} ` +
            `M${50 - cw * 0.9} ${f.crown + 22} h${cw * 1.8}" stroke="${hex(kit.metal)}" stroke-width="1.3" opacity="0.9"/>`),
      front: '',
    };
  }

  /** A hairline that has come away in places. Thin, not absent. */
  const patchy = face.hairGrowth === 'patchy';

  /** The cap every cut is built on: over the crown, down to a hairline. */
  const cap = (line: number, over: number): string =>
    `<path d="M${50 - w - over} ${line} C${50 - w - over - 0.6} ${f.crown - 6} ${50 - w * 0.5} ${f.crown - 8} 50 ${f.crown - 8} ` +
    `C${50 + w * 0.5} ${f.crown - 8} ${50 + w + over + 0.6} ${f.crown - 6} ${50 + w + over} ${line} ` +
    `C${50 + w * 0.55} ${line - 4} ${50 - w * 0.55} ${line - 4} ${50 - w - over} ${line} Z" fill="${col}"${patchy ? ' opacity="0.68"' : ''}/>` +
    (patchy
      ? `<path d="M${50 - w * 0.55} ${line - 3.4} a3 2.4 0 0 1 5 -0.6 M${50 + w * 0.2} ${line - 4} a3 2.4 0 0 1 5 -0.4" ` +
        `fill="${hex(shade(kit.hair, 0.6))}" opacity="0.5"/>`
      : '') +
    `<path d="M${50 + 1} ${f.crown - 7.4} C${50 + w * 0.7} ${f.crown - 7} ${50 + w + over} ${line - 4} ${50 + w + over} ${line} ` +
    `C${50 + w * 0.55} ${line - 4} ${50 + 4} ${line - 4.6} ${50 + 1} ${f.crown - 7.4} Z" fill="${litc}" opacity="0.45"/>`;

  switch (look.grooming) {
    case 'bald':
      return nothing;

    case 'shaved':
      // Not nothing: a shaved head still shows where the hair was.
      return { back: '', front: `<path d="${headPath(f)}" fill="${col}" opacity="0.13"/>` };

    case 'shaved-marked':
      return {
        back: '',
        front:
          `<path d="${headPath(f)}" fill="${col}" opacity="0.13"/>` +
          `<path d="M${50 - w * 0.6} ${f.crown + 3} C${50 - w * 0.2} ${f.crown + 7} ${50 - w * 0.2} ${brow - 6} ${50 - w * 0.55} ${brow - 3} ` +
          `M${50 + w * 0.6} ${f.crown + 3} C${50 + w * 0.2} ${f.crown + 7} ${50 + w * 0.2} ${brow - 6} ${50 + w * 0.55} ${brow - 3} ` +
          `M50 ${f.crown + 2} L50 ${brow - 4}" stroke="${hex(kit.accent)}" stroke-width="1.1" fill="none" opacity="0.55" stroke-linecap="round"/>`,
      };

    case 'tonsure':
      // A ring, and the bare crown inside it. The absence is the identifier.
      return {
        back: '',
        front:
          `<path d="M${50 - w - 1} ${brow + 2} C${50 - w - 1.5} ${f.crown - 2} ${50 + w + 1.5} ${f.crown - 2} ${50 + w + 1} ${brow + 2} ` +
          `C${50 + w * 0.5} ${brow - 1} ${50 - w * 0.5} ${brow - 1} ${50 - w - 1} ${brow + 2} Z" fill="${col}"/>`,
      };

    case 'cropped':
      // High at the temples and lower in the middle. The other way round is not
      // a receding hairline, it is a bowl cut with a dip in it.
      return { back: '', front: cap(brow - 3, 0.4) };

    case 'long':
      return {
        back:
          `<path d="M${50 - w - 2} ${f.chinY + 16} C${50 - w - 3} ${f.crown + 4} ${50 - w * 0.6} ${f.crown - 8} 50 ${f.crown - 8} ` +
          `C${50 + w * 0.6} ${f.crown - 8} ${50 + w + 3} ${f.crown + 4} ${50 + w + 2} ${f.chinY + 16} ` +
          `L${50 + w - 3} ${f.chinY + 16} C${50 + w - 2} ${f.jawY} ${50 + w * 0.5} ${f.crown + 1} 50 ${f.crown + 1} ` +
          `C${50 - w * 0.5} ${f.crown + 1} ${50 - w + 2} ${f.jawY} ${50 - w + 3} ${f.chinY + 16} Z" fill="${col}"/>`,
        front: cap(brow - 2, 0.8),
      };

    case 'braided':
      return {
        back:
          `<path d="M${50 - w - 1} ${f.cheekY - 2} L${50 - w - 4} ${f.chinY + 16} L${50 - w + 1.5} ${f.chinY + 16} L${50 - w + 2} ${f.cheekY - 2} Z" fill="${col}"/>` +
          `<path d="M${50 + w + 1} ${f.cheekY - 2} L${50 + w + 4} ${f.chinY + 16} L${50 + w - 1.5} ${f.chinY + 16} L${50 + w - 2} ${f.cheekY - 2} Z" fill="${litc}"/>` +
          // The bindings are what make a plait a plait rather than a rope.
          `<path d="M${50 - w - 1.4} ${f.jawY} l4 -0.4 M${50 - w - 2.2} ${f.chinY + 5} l4.4 -0.4 ` +
          `M${50 + w + 1.4} ${f.jawY} l-4 -0.4 M${50 + w + 2.2} ${f.chinY + 5} l-4.4 -0.4" ` +
          `stroke="${hex(kit.metal)}" stroke-width="1.2" opacity="0.85"/>`,
        front: cap(brow - 2, 0.6),
      };

    case 'topknot':
      return {
        back:
          `<ellipse cx="50" cy="${f.crown - 9}" rx="${w * 0.42}" ry="4.4" fill="${col}"/>` +
          `<ellipse cx="${50 + w * 0.12}" cy="${f.crown - 10}" rx="${w * 0.22}" ry="2.2" fill="${litc}" opacity="0.5"/>` +
          `<path d="M${50 - w * 0.3} ${f.crown - 6} L${50 - w * 0.5} ${f.chinY + 4} L${50 + w * 0.1} ${f.chinY + 2} L${50 + w * 0.1} ${f.crown - 6} Z" fill="${col}" opacity="0.9"/>`,
        front: cap(brow - 5, -0.6),
      };

    default:
      // wild: the cap plus locks breaking *sideways*. Upward would be horns.
      return {
        back:
          `<path d="M${50 - w - 1} ${f.cheekY - 6} L${50 - w - 9} ${f.cheekY + 4} L${50 - w - 3} ${f.cheekY + 3} L${50 - w - 7} ${f.jawY + 4} ` +
          `L${50 - w + 1} ${f.jawY} Z" fill="${col}"/>` +
          `<path d="M${50 + w + 1} ${f.cheekY - 6} L${50 + w + 9} ${f.cheekY + 2} L${50 + w + 3} ${f.cheekY + 3} L${50 + w + 8} ${f.jawY + 6} ` +
          `L${50 + w - 1} ${f.jawY} Z" fill="${litc}"/>`,
        front: cap(brow - 1, 1.2),
      };
  }
}

/** Beard, and it is a value on the jaw before it is ever a shape. */
function beardArt(f: Shape, kit: Kit, look: HouseLook): string {
  const col = hex(kit.hair);
  const j = f.jaw;
  const jawline =
    `M${50 - j - 0.5} ${f.jawY - 2} C${50 - j} ${f.chinY - 1} ${50 - f.chinW * 1.8} ${f.chinY + 1} 50 ${f.chinY + 1} ` +
    `C${50 + f.chinW * 1.8} ${f.chinY + 1} ${50 + j} ${f.chinY - 1} ${50 + j + 0.5} ${f.jawY - 2}`;
  const moustache =
    `<path d="M${50 - f.mouthW - 2.6} ${f.mouthY - 2.2} C${50 - 2} ${f.mouthY - 4.4} ${50 + 2} ${f.mouthY - 4.4} ${50 + f.mouthW + 2.6} ${f.mouthY - 2.2} ` +
    `C${50 + f.mouthW + 1} ${f.mouthY + 1.6} ${50 - f.mouthW - 1} ${f.mouthY + 1.6} ${50 - f.mouthW - 2.6} ${f.mouthY - 2.2} Z" fill="${col}"/>`;
  switch (look.beard) {
    case 'none':
      return '';
    case 'stubble':
      return (
        `<path d="${jawline} C${50 + j} ${f.jawY + 5} ${50 + f.chinW * 1.4} ${f.chinY - 4} 50 ${f.chinY - 4} ` +
        `C${50 - f.chinW * 1.4} ${f.chinY - 4} ${50 - j} ${f.jawY + 5} ${50 - j - 0.5} ${f.jawY - 2} Z" fill="${col}" opacity="0.26"/>`
      );
    case 'moustache':
      return moustache;
    case 'short':
      return (
        `<path d="${jawline} C${50 + j} ${f.jawY + 4} ${50 + f.chinW * 1.2} ${f.mouthY + 2} 50 ${f.mouthY + 2.4} ` +
        `C${50 - f.chinW * 1.2} ${f.mouthY + 2} ${50 - j} ${f.jawY + 4} ${50 - j - 0.5} ${f.jawY - 2} Z" fill="${col}"/>` +
        moustache
      );
    case 'forked':
      return (
        `<path d="M${50 - j - 1} ${f.jawY - 3} C${50 - j - 2} ${f.chinY + 6} ${50 - 7} ${f.chinY + 12} ${50 - 4} ${f.chinY + 18} ` +
        `L${50 - 1.5} ${f.chinY + 8} L${50 + 1.5} ${f.chinY + 8} L${50 + 4} ${f.chinY + 18} ` +
        `C${50 + 7} ${f.chinY + 12} ${50 + j + 2} ${f.chinY + 6} ${50 + j + 1} ${f.jawY - 3} ` +
        `C${50 + j} ${f.jawY + 4} ${50 + f.chinW} ${f.mouthY + 2} 50 ${f.mouthY + 2.4} ` +
        `C${50 - f.chinW} ${f.mouthY + 2} ${50 - j} ${f.jawY + 4} ${50 - j - 1} ${f.jawY - 3} Z" fill="${col}"/>` +
        moustache
      );
    case 'plaited':
      return (
        `<path d="M${50 - j - 1} ${f.jawY - 3} C${50 - j - 2} ${f.chinY + 4} ${50 - 6} ${f.chinY + 10} 50 ${f.chinY + 11} ` +
        `C${50 + 6} ${f.chinY + 10} ${50 + j + 2} ${f.chinY + 4} ${50 + j + 1} ${f.jawY - 3} ` +
        `C${50 + j} ${f.jawY + 4} ${50 + f.chinW} ${f.mouthY + 2} 50 ${f.mouthY + 2.4} ` +
        `C${50 - f.chinW} ${f.mouthY + 2} ${50 - j} ${f.jawY + 4} ${50 - j - 1} ${f.jawY - 3} Z" fill="${col}"/>` +
        `<path d="M${50 - 3.5} ${f.chinY + 11} L${50 - 4.5} ${f.chinY + 20} L${50 + 4.5} ${f.chinY + 20} L${50 + 3.5} ${f.chinY + 11} Z" fill="${col}"/>` +
        `<path d="M${50 - 4} ${f.chinY + 14} h8 M${50 - 4.3} ${f.chinY + 17.4} h8.6" stroke="${hex(kit.metal)}" stroke-width="1.2" opacity="0.85"/>` +
        moustache
      );
    default:
      // long
      return (
        `<path d="M${50 - j - 1} ${f.jawY - 4} C${50 - j - 2} ${f.chinY + 6} ${50 - 8} ${f.chinY + 18} 50 ${f.chinY + 21} ` +
        `C${50 + 8} ${f.chinY + 18} ${50 + j + 2} ${f.chinY + 6} ${50 + j + 1} ${f.jawY - 4} ` +
        `C${50 + j} ${f.jawY + 4} ${50 + f.chinW} ${f.mouthY + 2} 50 ${f.mouthY + 2.4} ` +
        `C${50 - f.chinW} ${f.mouthY + 2} ${50 - j} ${f.jawY + 4} ${50 - j - 1} ${f.jawY - 4} Z" fill="${col}"/>` +
        `<path d="M50 ${f.mouthY + 2.4} C${50 + f.chinW} ${f.mouthY + 2} ${50 + j} ${f.jawY + 4} ${50 + j + 1} ${f.jawY - 4} ` +
        `C${50 + j + 2} ${f.chinY + 6} ${50 + 8} ${f.chinY + 18} 50 ${f.chinY + 21} ` +
        `C${50 + 3} ${f.chinY + 12} ${50 + 4} ${f.chinY} ${50 + 2} ${f.mouthY + 4} Z" fill="${hex(kit.hairLight)}" opacity="0.4"/>` +
        moustache
      );
  }
}

/**
 * The shoulders, which are two fifths of the picture and usually an afterthought.
 *
 * Every one of these runs off both edges of the frame. A mantle that stops
 * inside the arch reads as a bust on a plinth rather than as a person the frame
 * has cropped, and the crop is what makes a portrait a portrait.
 */
function mantleArt(f: Shape, kit: Kit, face: RaceFace, look: HouseLook): string {
  const sw = 44 * face.shoulder;
  const y = 120 - Math.min(34, 26 * face.shoulder);
  const cloth = hex(kit.cloth);
  const clothLit = hex(kit.clothLight);
  const metal = hex(kit.metal);
  const lit = hex(kit.lit);
  const deep = hex(kit.deep);
  const body =
    `<path d="M${50 - sw} 120 C${50 - sw + 2} ${y + 4} ${50 - f.neck * 2.4} ${y} 50 ${y} ` +
    `C${50 + f.neck * 2.4} ${y} ${50 + sw - 2} ${y + 4} ${50 + sw} 120 Z" fill="${cloth}"/>` +
    `<path d="M50 ${y} C${50 + f.neck * 2.4} ${y} ${50 + sw - 2} ${y + 4} ${50 + sw} 120 L${50 + sw - 12} 120 ` +
    `C${50 + sw - 16} ${y + 8} ${50 + 6} ${y + 3} 50 ${y + 2} Z" fill="${clothLit}"/>`;

  switch (look.mantle) {
    case 'bare':
      // Skin, not cloth. Needs a collarbone or the chest is a slab.
      return (
        `<path d="M${50 - sw} 120 C${50 - sw + 2} ${y + 4} ${50 - f.neck * 2.4} ${y} 50 ${y} ` +
        `C${50 + f.neck * 2.4} ${y} ${50 + sw - 2} ${y + 4} ${50 + sw} 120 Z" fill="${hex(face.skin[0])}"/>` +
        `<path d="M50 ${y} C${50 + f.neck * 2.4} ${y} ${50 + sw - 2} ${y + 4} ${50 + sw} 120 L${50 + sw - 12} 120 ` +
        `C${50 + sw - 16} ${y + 8} ${50 + 6} ${y + 3} 50 ${y + 2} Z" fill="${hex(mix(face.skin[0], 0xffffff, 0.12))}"/>` +
        `<path d="M${50 - 16} ${y + 7} C${50 - 8} ${y + 11} ${50 + 8} ${y + 11} ${50 + 16} ${y + 7}" ` +
        `stroke="${hex(face.skin[2])}" stroke-width="1" fill="none" opacity="0.4"/>`
      );
    case 'gorget':
      return (
        body +
        `<path d="M${50 - 22} ${y + 6} C${50 - 14} ${y - 2} ${50 + 14} ${y - 2} ${50 + 22} ${y + 6} ` +
        `L${50 + 24} ${y + 14} C${50 + 14} ${y + 6} ${50 - 14} ${y + 6} ${50 - 24} ${y + 14} Z" fill="${metal}"/>` +
        `<path d="M${50 - 22} ${y + 6} C${50 - 14} ${y - 2} ${50 + 14} ${y - 2} ${50 + 22} ${y + 6} ` +
        `L${50 + 23} ${y + 9} C${50 + 14} ${y + 1} ${50 - 14} ${y + 1} ${50 - 23} ${y + 9} Z" fill="${lit}" opacity="0.6"/>` +
        `<path d="M50 ${y + 10} L${50 + 6} ${y + 18} L50 ${y + 30} L${50 - 6} ${y + 18} Z" fill="${hex(kit.accent)}"/>`
      );
    case 'pauldrons':
      return (
        body +
        `<path d="M${50 - sw - 2} 120 C${50 - sw - 2} ${y + 8} ${50 - sw + 8} ${y - 4} ${50 - sw + 20} ${y - 2} ` +
        `C${50 - sw + 14} ${y + 12} ${50 - sw + 12} ${y + 22} ${50 - sw + 12} 120 Z" fill="${deep}"/>` +
        `<path d="M${50 + sw + 2} 120 C${50 + sw + 2} ${y + 8} ${50 + sw - 8} ${y - 4} ${50 + sw - 20} ${y - 2} ` +
        `C${50 + sw - 14} ${y + 12} ${50 + sw - 12} ${y + 22} ${50 + sw - 12} 120 Z" fill="${lit}"/>` +
        `<path d="M${50 + sw - 20} ${y - 2} L${50 + sw - 14} ${y + 12} L${50 + sw - 8} ${y + 10} L${50 + sw - 15} ${y - 3} Z" fill="${hex(kit.accent)}"/>`
      );
    case 'pelt':
      return (
        `<path d="M${50 - sw - 4} 120 C${50 - sw - 3} ${y - 2} ${50 - 16} ${y - 6} 50 ${y - 4} ` +
        `C${50 + 16} ${y - 6} ${50 + sw + 3} ${y - 2} ${50 + sw + 4} 120 Z" fill="${hex(shade(kit.cloth, 0.7))}"/>` +
        body +
        // Fur as five wedges. Five reads; fifty is grey.
        `<path d="M${50 - sw + 4} 120 L${50 - sw + 12} ${y + 6} L${50 - sw + 20} 120 L${50 - 10} ${y + 8} L${50 + 6} 120 ` +
        `L${50 + sw - 20} ${y + 6} L${50 + sw - 10} 120 Z" fill="${hex(mix(kit.cloth, 0xd8cbb0, 0.3))}" opacity="0.5"/>`
      );
    case 'scale':
      return (
        body +
        `<path d="M${50 - 30} ${y + 8} a5 5 0 0 1 10 0 M${50 - 20} ${y + 8} a5 5 0 0 1 10 0 M${50 - 10} ${y + 8} a5 5 0 0 1 10 0 ` +
        `M50 ${y + 8} a5 5 0 0 1 10 0 M${50 + 10} ${y + 8} a5 5 0 0 1 10 0 M${50 + 20} ${y + 8} a5 5 0 0 1 10 0 ` +
        `M${50 - 25} ${y + 15} a5 5 0 0 1 10 0 M${50 - 15} ${y + 15} a5 5 0 0 1 10 0 M${50 - 5} ${y + 15} a5 5 0 0 1 10 0 ` +
        `M${50 + 5} ${y + 15} a5 5 0 0 1 10 0 M${50 + 15} ${y + 15} a5 5 0 0 1 10 0" ` +
        `stroke="${metal}" stroke-width="1.1" fill="none" opacity="0.75"/>`
      );
    case 'harness':
      return (
        body +
        `<path d="M${50 - 26} ${y + 2} L${50 + 14} 120 L${50 + 4} 120 L${50 - 34} ${y + 5} Z" fill="${hex(shade(kit.cloth, 0.6))}"/>` +
        `<path d="M${50 + 26} ${y + 2} L${50 - 14} 120 L${50 - 4} 120 L${50 + 34} ${y + 5} Z" fill="${hex(mix(kit.cloth, 0xffffff, 0.1))}"/>` +
        `<circle cx="50" cy="${y + 16}" r="4.6" fill="${metal}"/>` +
        `<circle cx="50" cy="${y + 16}" r="2" fill="${hex(kit.accent)}"/>`
      );
    case 'chasuble':
      return (
        body +
        `<path d="M${50 - 12} ${y + 1} C${50 - 16} ${y + 18} ${50 - 20} ${y + 28} ${50 - 20} 120 L${50 - 34} 120 ` +
        `C${50 - 34} ${y + 20} ${50 - 24} ${y + 5} ${50 - 18} ${y + 1} Z" fill="${clothLit}"/>` +
        `<path d="M${50 + 12} ${y + 1} C${50 + 16} ${y + 18} ${50 + 20} ${y + 28} ${50 + 20} 120 L${50 + 34} 120 ` +
        `C${50 + 34} ${y + 20} ${50 + 24} ${y + 5} ${50 + 18} ${y + 1} Z" fill="${clothLit}"/>` +
        `<path d="M${50 - 4} ${y + 2} L${50 + 4} ${y + 2} L${50 + 4} 120 L${50 - 4} 120 Z" fill="${metal}" opacity="0.85"/>` +
        `<path d="M${50 - 12} ${y + 16} L${50 + 12} ${y + 16} L${50 + 12} ${y + 22} L${50 - 12} ${y + 22} Z" fill="${metal}" opacity="0.85"/>`
      );
    default:
      // robe
      return (
        body +
        `<path d="M50 ${y + 1} L${50 + 15} ${y + 12} L${50 + 9} 120 L${50 - 9} 120 L${50 - 15} ${y + 12} Z" fill="${hex(shade(kit.cloth, 0.62))}"/>` +
        `<path d="M50 ${y + 1} L${50 + 15} ${y + 12} L${50 + 12} ${y + 30} L50 ${y + 14} Z" fill="${clothLit}" opacity="0.5"/>` +
        `<path d="M50 ${y + 14} L${50 + 5} ${y + 22} L50 ${y + 34} L${50 - 5} ${y + 22} Z" fill="${hex(kit.accent)}"/>`
      );
  }
}

/**
 * One asymmetric thing that belongs to this leader and to nobody else.
 *
 * Asymmetry is the whole value. A symmetrical mark reads as ornament and gets
 * filed with the crown; a mark on one side reads as something that happened to
 * a person. It is also why `mark` is in `HouseLook` and never in `RaceFace` —
 * a people does not have a scar.
 */
function markArt(f: Shape, s: Skin, kit: Kit, look: HouseLook): string {
  const c = f.cheek;
  switch (look.mark) {
    case 'none':
      return '';
    case 'scar':
      // Across the cheekbone at an angle, stopping short of the jaw. Vertical
      // and full-length, it is a line on a picture rather than a scar on a man.
      return (
        `<path d="M${50 + c * 0.78} ${f.eyeY - 4} L${50 + c * 0.42} ${f.noseY + 2}" ` +
        `stroke="${hex(0x9a6a58)}" stroke-width="1" stroke-linecap="round" opacity="0.55"/>` +
        `<path d="M${50 + c * 0.66} ${f.eyeY - 1.2} l2 0.8 M${50 + c * 0.55} ${f.eyeY + 2} l2 0.8" ` +
        `stroke="${hex(0x8f5a4a)}" stroke-width="0.7" stroke-linecap="round" opacity="0.6"/>`
      );
    case 'blind-eye':
      return (
        `<ellipse cx="${50 - f.eyeDx}" cy="${f.eyeY}" rx="${f.eyeW * 0.9}" ry="${f.eyeH * 1.5}" fill="${hex(0xcfc7b6)}" opacity="0.82"/>` +
        `<path d="M${50 - f.eyeDx - f.eyeW - 2} ${f.eyeY - 5} L${50 - f.eyeDx + f.eyeW + 1} ${f.eyeY + 5}" ` +
        `stroke="${hex(0x8a6a58)}" stroke-width="1.1" stroke-linecap="round" opacity="0.7"/>`
      );
    case 'brand':
      return (
        `<path d="M${50 + c * 0.5} ${f.cheekY + 4} l4 0 l-2 3.4 l4 0" stroke="${hex(kit.accent)}" ` +
        `stroke-width="1.2" fill="none" opacity="0.7" stroke-linecap="round" stroke-linejoin="round"/>`
      );
    case 'broken-tusk':
      // Only means anything on a face that has tusks; on one that has not, it
      // is a chipped lip, which is close enough and costs nothing to allow.
      return (
        `<path d="M${50 - f.mouthW * 0.82} ${f.mouthY + 1.5} L${50 - f.mouthW * 0.82 - 1.2} ${f.mouthY - 2} L${50 - f.mouthW * 0.82 + 1.6} ${f.mouthY + 1} Z" ` +
        `fill="${hex(s.shade)}"/>`
      );
    case 'warpaint':
      // A band across the eyes, which is the one mark that survives at 30px.
      return (
        `<path d="M${50 - c - 1} ${f.eyeY - 3.5} L${50 + c + 1} ${f.eyeY - 4.5} L${50 + c + 1} ${f.eyeY + 2.5} L${50 - c - 1} ${f.eyeY + 3.5} Z" ` +
        `fill="${hex(kit.accent)}" opacity="0.38"/>`
      );
    case 'tattoo':
      return (
        `<path d="M${50 - c * 0.7} ${f.cheekY + 3} C${50 - c * 0.35} ${f.cheekY + 7} ${50 - c * 0.35} ${f.jawY - 4} ${50 - c * 0.6} ${f.jawY - 1} ` +
        `M${50 - c * 0.45} ${f.cheekY + 5} C${50 - c * 0.2} ${f.cheekY + 9} ${50 - c * 0.2} ${f.jawY - 4} ${50 - c * 0.38} ${f.jawY - 2}" ` +
        `stroke="${hex(kit.accent)}" stroke-width="0.9" fill="none" opacity="0.5" stroke-linecap="round"/>`
      );
    default:
      // burn: a soft-edged patch rather than a line, because a burn has no edge.
      return (
        `<ellipse cx="${50 + c * 0.55}" cy="${f.cheekY + 6}" rx="${c * 0.36}" ry="7" fill="${hex(s.deep)}" opacity="0.34"/>` +
        `<ellipse cx="${50 + c * 0.52}" cy="${f.cheekY + 5}" rx="${c * 0.2}" ry="4" fill="${hex(s.deep)}" opacity="0.3"/>`
      );
  }
}

/* ── Assembly ────────────────────────────────────────────────────────────── */

export interface LeaderPortraitOptions {
  /** Pixel width of the square. Height is size * 1.2, as with `heroPortrait`. */
  size?: number;
  /** Draw the gilded arch. Off for tiny inline uses. */
  frame?: boolean;
  /** Dim everything but the silhouette — an unchosen or locked kingdom. */
  muted?: boolean;
}

/**
 * One kingdom's leader, as an SVG string ready for `innerHTML`.
 *
 * Self-contained: no external ids, no shared defs, no CSS dependencies, so
 * twenty-two of them can sit on one selection screen without colliding. Every
 * gradient id is keyed off `look.id`, and the same pair of records always
 * produces byte-identical output, which is what makes the screen diffable.
 *
 * The order of the layers is the same as the heroes' and it is load-bearing:
 * mantle-behind, hair behind, crown behind, neck, then the head group — head,
 * ears, features, hair in front, crown in front, mark — then the mantle, then
 * the light over the lot. Neck before head or the neck fills over the jaw;
 * anything worn on the head after the head or the skull covers it.
 */
export function leaderPortrait(face: RaceFace, look: HouseLook, opts: LeaderPortraitOptions = {}): string {
  const f = shapeOf(face, look);
  const s = skinOf(face);
  const kit = kitOf(face, look);
  const size = opts.size ?? 96;
  const frame = opts.frame !== false;
  const uid = `lp-${look.id}`;
  const seed = idHash(look.id);

  const crown = crownArt(f, kit, look);
  const groom = groomingArt(f, kit, face, look);
  const crest = crestArt(f, kit, face);
  const iris = face.iris.length > 0 ? (face.iris[Math.floor(seed * face.iris.length)] ?? face.iris[0]!) : 0x5f4a32;

  const eyeLook: FaceLook = {
    hair: kit.hair,
    brow: shade(kit.hair, 0.85),
    eye: iris,
    cloth: kit.cloth,
    gaze: look.bearing.gaze,
    pupil: face.pupil,
    sclera: face.sclera,
    glow: face.glow,
  };

  // The crop is `frameScale`, and it is the roster's strongest single lever:
  // spreading it 0.74 to 1.24 is what stops eleven peoples reading as eleven
  // palettes at thumbnail size.
  const crop = 1.16 * face.frameScale;

  // ── Headroom, and it has to be solved rather than nudged ──────────────────
  //
  // A mitre, a rack of antlers and a ram's horn all need different amounts of
  // sky, so the first cut computed a vertical offset instead of authoring
  // twenty-two of them by hand. That was the right instinct and the wrong
  // arithmetic: the offset was clamped at 18, and past that the crown simply
  // left the frame. Measured across the roster, **three houses were clipped** —
  // the Vigil's mitre at 3.7, the Litany's horned helm at -1.8 and the Moonbound
  // antler-rack at -10.9, against an arch that tops out at 6. Not clipped a
  // little: gone, silently, taking with it the single strongest identifier of
  // the three houses that leant on it hardest. `newgame` hit the same fault in
  // their stand-in and said so, which is the only reason it was measured here
  // rather than discovered on the selection screen.
  //
  // The fix is to give the ornament, not the head, whatever has to yield. Head
  // size *is* `frameScale` and `frameScale` is race identity — shrinking the
  // crop to fit a hat would make a Moonbound smaller than a Moonbound, and make
  // one of the two Moonbound kingdoms smaller than the other. So the head keeps
  // its scale and a piece of headgear too tall for the frame is scaled down
  // about the crown until it fits. A mitre at 94% is a mitre; an antler rack at
  // 53% is still unmistakably an antler rack, and either beats an empty sky.
  const CLEAR = 9;
  const TARGET = 14;
  const DY_MIN = -6;
  const DY_MAX = 22;
  const need = Math.max(crown.rise, face.crest === 'none' ? 0 : face.crestSize * 6, 8);
  const sky = (46 + DY_MAX - CLEAR) / crop - (55 - f.crown);
  const orn = need > sky ? Math.max(0.42, sky / need) : 1;
  const top = f.crown - need * orn;
  const cropDy = Math.max(DY_MIN, Math.min(DY_MAX, TARGET - 46 + crop * (55 - top)));
  const pivot = `${50} ${f.chinY + 8}`;
  /** Wrap headgear so it shrinks about the crown rather than leaving the frame. */
  const fit = (art: string): string =>
    orn >= 0.999 || art === ''
      ? art
      : `<g transform="translate(50 ${f.crown}) scale(${orn.toFixed(3)}) translate(-50 ${-f.crown})">${art}</g>`;

  return `
<svg viewBox="0 0 100 120" width="${size}" height="${size * 1.2}" role="img"
     data-lp="${look.id}" data-lpmuted="${opts.muted ? 1 : 0}"
     aria-label="${look.leader}" style="display:block${opts.muted ? ';filter:saturate(0.42) brightness(0.94)' : ''}">
  <defs>
    <radialGradient id="${uid}-glow" cx="50%" cy="36%" r="62%">
      <stop offset="0%" stop-color="${hex(kit.accent)}" stop-opacity="0.5"/>
      <stop offset="55%" stop-color="${hex(kit.accent)}" stop-opacity="0.12"/>
      <stop offset="100%" stop-color="${hex(C.ashDeep)}" stop-opacity="0"/>
    </radialGradient>
    <linearGradient id="${uid}-key" x1="0" y1="0" x2="1" y2="0.35">
      <stop offset="0%" stop-color="#000" stop-opacity="0.38"/>
      <stop offset="42%" stop-color="#000" stop-opacity="0.1"/>
      <stop offset="72%" stop-color="#000" stop-opacity="0"/>
      <stop offset="100%" stop-color="${hex(C.goldPale)}" stop-opacity="0.2"/>
    </linearGradient>
    <linearGradient id="${uid}-sink" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${hex(C.ashDeep)}" stop-opacity="0"/>
      <stop offset="70%" stop-color="${hex(C.ashDeep)}" stop-opacity="0.08"/>
      <stop offset="100%" stop-color="${hex(C.ashDeep)}" stop-opacity="0.6"/>
    </linearGradient>
    <linearGradient id="${uid}-niche" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${hex(mix(C.ashLight, kit.accent, 0.16))}"/>
      <stop offset="100%" stop-color="${hex(C.ashDeep)}"/>
    </linearGradient>
    <clipPath id="${uid}-clip"><path d="${ARCH}"/></clipPath>
  </defs>

  <path d="${ARCH}" fill="url(#${uid}-niche)"/>
  <g clip-path="url(#${uid}-clip)">
    <rect x="0" y="0" width="100" height="120" fill="url(#${uid}-glow)"/>
    <g transform="translate(50 ${46 + cropDy}) scale(${crop.toFixed(4)}) translate(-50 -55)">
      <g transform="rotate(${f.tilt} ${pivot})">
        ${fit(crest)}
        ${fit(crown.behind)}
        ${groom.back}
      </g>
      ${neck(f, s)}
      <g transform="rotate(${f.tilt} ${pivot})">
        ${raceEars(f, s, face)}
        ${modelling(f, s, uid)}
        ${hideMarks(f, s, face)}
        ${eyes(f, eyeLook, s, uid)}
        ${face.muzzle > 0.15 ? muzzleArt(f, s, face.muzzle) : ''}
        ${features(f, s, { muzzle: face.muzzle, lip: face.lip })}
        ${tusks(f, face)}
        ${groom.front}
        ${beardArt(f, kit, look)}
        ${fit(crown.front)}
        ${markArt(f, s, kit, look)}
      </g>
      ${mantleArt(f, kit, face, look)}
    </g>
    <rect x="0" y="0" width="100" height="120" fill="url(#${uid}-key)"/>
    <rect x="0" y="0" width="100" height="120" fill="url(#${uid}-sink)"/>
    <rect x="0" y="0" width="100" height="120" fill="url(#${uid}-glow)" opacity="0.28"
          style="mix-blend-mode:screen"/>
  </g>
  ${
    frame
      ? `<path d="M6 120 L6 52 C6 24 26 6 50 6 C74 6 94 24 94 52 L94 120"
                fill="none" stroke="${hex(C.goldDeep)}" stroke-width="3"/>
         <path d="M8.5 120 L8.5 52 C8.5 25.5 27 8.5 50 8.5 C73 8.5 91.5 25.5 91.5 52 L91.5 120"
                fill="none" stroke="${hex(C.gold)}" stroke-width="1.2" opacity="0.85"/>`
      : ''
  }
</svg>`;
}
